import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';

import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Tabs,
  message,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  notification,
  Descriptions,
  Alert,
  Tooltip,
  InputNumber,
  Popconfirm,
  Upload,
  Divider,
  List,
  Collapse,
  Checkbox,
  Dropdown,
  AutoComplete,
  TreeSelect,
} from 'antd';
import {
  InboxOutlined,
  ClockCircleOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UploadOutlined,
  AlertOutlined,
  FileTextOutlined,
  PrinterOutlined,
  SettingOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  UpOutlined,
  DownOutlined,
  ApartmentOutlined,
  BuildOutlined,
  BarsOutlined,
  DatabaseOutlined,
  BoxPlotOutlined,
  HomeOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import useDraggableColumns from '../../../components/table/useDraggableColumns';
import dayjs from 'dayjs';
import { PersistentStorage } from '../../../utils/persistentStorage';
import { useIsMobile, getMobileButtonProps } from '../../../utils/responsiveUtils';
import InboundBulkUploadModal from '../components/InboundBulkUploadModal';
import apiService from '../../../services/apiService';
import {generateInboundVoucherPDF, generateInboundBeePDF} from "@utils/pdfUtils.ts";


const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Search } = Input;
const { TabPane } = Tabs;

const ALL_COLUMNS_VERSION = '2024-10-received';

// 整合的入庫訂單接口
interface InboundOrder {
  id: string;
  orderNumber: string; // 入庫單號
  customer: string; // 客戶
  customerName: string;
  warehouse: string; // 倉庫
  warehouseName: string;
  status: 'new' | 'received_quantity_correct' | 'received_quantity_incorrect';
  expectedQuantity: number;
  confirmedQuantity?: number;
  lotNumber?: string;
  serialNumber?: string;
  dimensions?: { length: number; width: number; height: number; unit: string };
  baseUnit: string;
  packagingUnit: string;
  logisticsMethod?: string;
  trackingNumber?: string;
  referenceNumber?: string;
  expectedArrivalDate: Date;
  receiptDate?: Date;
  shipperContactName?: string;
  shipperContactPhone?: string;
  recipient?: string;
  recipientAddress?: { street: string; city: string; state: string; postalCode: string; country: string };
  recipientPhone?: string;
  cargoRemark?: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  barcode?: string;
  qrCode?: string;
  items?: InboundItem[];
}

interface InboundItem {
  id: string;
  orderId: string;
  productSku: string;
  productName: string;
  lotNumber: string;
  serialNumber: string;
  expiryDate?: string;
  temperatureRequirement?: string; // 保留舊字段以相容舊資料
  temperatureId?: string; // 新：溫度主檔Id
  warehouse: string;
  expectedQuantity: number;
  confirmedQuantity: number;
  currentLocation: string;
  currentStock: number;
  remark: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  packageCBM?: number; // 新增 包裝CBM 字段 (立方米)
  packagingUnit?: string; // 新增 包裝單位 字段
  baseUnit: string; // 新增 baseUnit 字段
  userName: string;
  updateDateTime: string;
}

interface Warehouse {
  id: string;
  name: string;
  code: string;
}

interface Location {
  id: string;
  name: string;
  code: string;
  warehouseId: string;
  parent_id?: string;
}

type InboundLocationType = 'ZONE' | 'FLOOR' | 'AISLE' | 'RACK' | 'SHELF' | 'BIN' | 'UNKNOWN';

const getInboundLocationDisplayConfig = (
  node: any
): { icon: React.ReactNode; color: string; label: string; type: InboundLocationType } => {
  const rawType = node?.locationType ?? node?.location_type ?? node?.type ?? '';
  const upperType = rawType.toString().toUpperCase();

  if (upperType === 'ZONE') return { icon: <ApartmentOutlined />, color: 'blue', label: '區域', type: 'ZONE' };
  if (upperType === 'FLOOR') return { icon: <BuildOutlined />, color: 'gold', label: '樓層', type: 'FLOOR' };
  if (upperType === 'AISLE') return { icon: <BarsOutlined />, color: 'green', label: '通道', type: 'AISLE' };
  if (upperType === 'RACK') return { icon: <DatabaseOutlined />, color: 'orange', label: '貨架', type: 'RACK' };
  if (upperType === 'SHELF') return { icon: <BoxPlotOutlined />, color: 'purple', label: '層', type: 'SHELF' };
  if (upperType === 'BIN') return { icon: <HomeOutlined />, color: 'red', label: '儲位', type: 'BIN' };

  if (node?.bin) return { icon: <HomeOutlined />, color: 'red', label: '儲位', type: 'BIN' };
  if (node?.shelf) return { icon: <BoxPlotOutlined />, color: 'purple', label: '層', type: 'SHELF' };
  if (node?.rack) return { icon: <DatabaseOutlined />, color: 'orange', label: '貨架', type: 'RACK' };
  if (node?.aisle) return { icon: <BarsOutlined />, color: 'green', label: '通道', type: 'AISLE' };

  return { icon: <FolderOutlined />, color: 'default', label: upperType || '其他', type: 'UNKNOWN' };
};

const InboundManagement: React.FC = (): JSX.Element => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<InboundOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<InboundOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<InboundOrder | null>(null);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InboundItem | null>(null);
  const [form] = Form.useForm();
  const [itemForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [orderItems, setOrderItems] = useState<InboundItem[]>([]);
  const [editingItemIndex, setEditingItemIndex] = useState<number>(-1);
  const [fieldConfigModalVisible, setFieldConfigModalVisible] = useState(false);
  const [fieldConfigForm] = Form.useForm();
  // 只讀模式（用編輯表單作為詳情視圖）
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);
  // 收貨模態框相關狀態
  const [receiptModalVisible, setReceiptModalVisible] = useState(false);
  const [selectedReceiptOrder, setSelectedReceiptOrder] = useState<InboundOrder | null>(null);
  const [receiptItems, setReceiptItems] = useState<InboundItem[]>([]);
  const [receiptForm] = Form.useForm();
  const [receiptItemForm] = Form.useForm();
  const [receiptItemModalVisible, setReceiptItemModalVisible] = useState(false);
  const [editingReceiptItemIndex, setEditingReceiptItemIndex] = useState<number>(-1);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [selectedReceiptItem, setSelectedReceiptItem] = useState<InboundItem | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [columnSettingsVisible, setColumnSettingsVisible] = useState(false);
  const [temperatureOptions, setTemperatureOptions] = useState<Array<{ id: string; code: string; name: string }>>([]);
  const [products, setProducts] = useState<Array<{ id: string; sku: string; name: string; length?: number | null; width?: number | null; height?: number | null; weight?: number | null; volume?: number | null; packagingUnit?: string | null; baseUnit?: string | null }>>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [editingItemExpectedQty, setEditingItemExpectedQty] = useState<number>(1);
  // 分頁狀態（服務端分頁）
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [total, setTotal] = useState<number>(0);

  const persistentStoreRef = React.useRef<InboundOrder[]>([]);
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Function to load initial products (limited set for better performance)
  const loadInitialProducts = useCallback(async () => {
    try {
      setProductsLoading(true);
      // Only load a small initial set for better performance
      const resp: any = await apiService.getProductLookups({ limit: 100 });
      const list = (resp?.products || resp?.data?.products || []) as any[];
      setProducts(list.map((p: any) => ({
        id: p.productId || p.id,
        sku: p.sku || p.itemCode,
        name: p.name || p.itemName,
        length: p.length ?? null,
        width: p.width ?? null,
        height: p.height ?? null,
        weight: p.weight ?? null,
        volume: p.volume ?? null,
        packagingUnit: p.packagingUnit ?? null,
        baseUnit: p.baseUnit ?? null,
      })));
    } catch (e) {
      message.error('載入產品清單失敗');
    } finally {
      setProductsLoading(false);
    }
  }, []);

  // Load initial products on mount
  useEffect(() => {
    loadInitialProducts();
  }, [loadInitialProducts]);

  // Remote search for products (for handling 12000+ products)
  const handleProductSearch = useCallback((value: string) => {
    // If search is empty or too short, reload initial products
    if (!value || value.trim().length < 2) {
      // Clear any pending searches
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      // Reset to initial products when search is cleared
      loadInitialProducts();
      return;
    }

    // Clear any pending searches
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce the search
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setProductsLoading(true);
        const resp: any = await apiService.getProductLookups({ search: value, limit: 100 });
        const list = (resp?.products || resp?.data?.products || []) as any[];
        const searchResults = list.map((p: any) => ({
          id: p.productId || p.id,
          sku: p.sku || p.itemCode,
          name: p.name || p.itemName,
          length: p.length ?? null,
          width: p.width ?? null,
          height: p.height ?? null,
          weight: p.weight ?? null,
          volume: p.volume ?? null,
          packagingUnit: p.packagingUnit ?? null,
          baseUnit: p.baseUnit ?? null,
        }));

        // Replace with search results only (show only matching products)
        setProducts(searchResults);
      } catch (e) {
        console.error('搜尋產品失敗:', e);
      } finally {
        setProductsLoading(false);
      }
    }, 500);
  }, [loadInitialProducts]);
  

  // 防抖產品搜尋
  const debounceUpdateProduct = useCallback((
    formInstance: any,
    sourceField: 'sku' | 'name',
    targetField: 'productName' | 'productSku',
    value: string
  ) => {
    if (!value) {
      formInstance.setFieldsValue({ [targetField]: '' });
      return;
    }
    
    const product = sourceField === 'sku' 
      ? products.find(p => p.sku === value)
      : products.find(p => p.name === value);
      
    if (product) {
      const targetValue = targetField === 'productName' ? product.name : product.sku;
      formInstance.setFieldsValue({ [targetField]: targetValue });
    }
  }, []);

  // 防抖函數
  const debounce = useCallback((func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  // 創建防抖版本的產品更新函數
  const debouncedUpdateProductFromSku = useCallback(
    debounce((formInstance: any, value: string) => {
      debounceUpdateProduct(formInstance, 'sku', 'productName', value);
    }, 300), [debounceUpdateProduct, debounce]
  );

  // 模擬從API獲取的產品和客戶設置
  // TODO: 這些數據將從產品API和客戶API獲取
  const [productSettings] = useState({
    baseUnit: 'PCS', // 從產品設置API獲取
    packagingUnit: 'BOX', // 從產品設置API獲取
    conversionFactor: 24 // 從產品設置API獲取
  });

  const [customerSettings] = useState({
    preferredLogistics: 'SF Express', // 從客戶設置API獲取
    contactPerson: 'John Doe', // 從客戶設置API獲取
    contactPhone: '+852 1234 5678' // 從客戶設置API獲取
  });

  // 模擬從API獲取的倉庫設置
  // TODO: 這些數據將從倉庫API獲取
  const [warehouseSettings] = useState({
    recipient: 'Shoptoplus', // 從倉庫設置API獲取
    recipientPhone: '+852 3456 7890', // 從倉庫設置API獲取
    recipientAddress: '123 Industrial Ave, Hong Kong, HK 000000, Hong Kong' // 從倉庫設置API獲取
  });

  // 列配置狀態 - 匹配新的14個預定入庫字段
  const [columnSettings, setColumnSettings] = useState(() => {
    const saved = localStorage.getItem('inbound-columns-config');
    const version = localStorage.getItem('inbound-columns-version');
    
    // 檢查版本，如果不是最新版本則重置配置
    if (version !== ALL_COLUMNS_VERSION || !saved) {
      const defaultConfig = {
        orderNumber: { visible: true, order: 0 },
        customerName: { visible: true, order: 1 },
        warehouseName: { visible: true, order: 2 },
        status: { visible: true, order: 3 },
        expectedQuantity: { visible: true, order: 4 },
        confirmedQuantity: { visible: true, order: 5 },
        logisticsMethod: { visible: true, order: 6 },
        trackingNumber: { visible: false, order: 7 },
        expectedArrivalDate: { visible: true, order: 8 },
        shipperContactName: { visible: false, order: 9 },
        shipperContactPhone: { visible: false, order: 10 },
        recipient: { visible: false, order: 11 },
        recipientAddress: { visible: false, order: 12 },
        recipientPhone: { visible: false, order: 13 },
        cargoRemark: { visible: false, order: 14 },
        actions: { visible: true, order: 15 }
      };
      localStorage.setItem('inbound-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('inbound-columns-version', ALL_COLUMNS_VERSION);
      return defaultConfig;
    }
    
    // 解析保存的配置，但只保留我們支持的列
    const parsedConfig = JSON.parse(saved);
    const validKeys = ['orderNumber', 'customerName', 'warehouseName', 'status', 
                      'expectedQuantity', 'confirmedQuantity', 'logisticsMethod', 'trackingNumber', 'expectedArrivalDate', 'shipperContactName', 
                      'shipperContactPhone', 'recipient', 'recipientAddress', 'recipientPhone', 
                      'cargoRemark', 'actions'];
    
    const cleanedConfig = validKeys.reduce((acc, key) => {
      acc[key] = parsedConfig[key] || { visible: false, order: validKeys.indexOf(key) };
      return acc;
    }, {} as any);
    
    return cleanedConfig;
  });
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);

  // 展開收起控制
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const toggleCollapsed = () => setCollapsed(prev => !prev);

  // 模擬數據（保留倉庫/庫位），產品數據改為從 API 載入
  const mockWarehouses: Warehouse[] = [
    { id: '1', name: 'Main Warehouse', code: 'MW' },
    { id: '2', name: 'Secondary Warehouse', code: 'SW' },
    { id: '3', name: 'Distribution Center', code: 'DC' },
  ];

  // State for warehouse locations (fetched from API)
  const [warehouseLocations, setWarehouseLocations] = useState<Location[]>([]);
  const [locationTreeData, setLocationTreeData] = useState<any[]>([]);
  const [locationLabelMap, setLocationLabelMap] = useState<Record<string, string>>({});
  const [locationsLoading, setLocationsLoading] = useState<boolean>(false);
  const companyIdForInbound = '1';

  // Function to fetch warehouse locations based on warehouse ID
  const fetchWarehouseLocations = useCallback(async (warehouseId: string) => {
    if (!warehouseId) {
      setWarehouseLocations([]);
      setLocationTreeData([]);
      setLocationLabelMap({});
      return;
    }

    try {
      setLocationsLoading(true);
      const response: any = await apiService.getWarehouseLocations(warehouseId, {
        flat: false,
        companyId: companyIdForInbound,
      });

      const hierarchy = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

      const formatLocationPath = (segments: string[]): string => {
        if (!Array.isArray(segments) || segments.length === 0) return '';

        if (segments.length === 1) {
          return segments[0];
        }

        if (segments.length === 2) {
          return segments.join('/');
        }

        // ⬇️ 當長度大於 2
        const firstThree = segments.slice(0, 3).join('/'); // 前三個用 "/"
        const rest = segments.slice(3).join('-');          // 後面的用 "-"
        return rest ? `${firstThree}/${rest}` : firstThree;
      };


      const buildTree = (
        nodes: any[],
        parentCodes: string[] = [],
        labelMap: Record<string, string>
      ): any[] => {
        if (!Array.isArray(nodes)) return [];
        const sortedNodes = [...nodes].sort((a, b) => {
          const aLevel = Number(a.level ?? a.levels ?? 0);
          const bLevel = Number(b.level ?? b.levels ?? 0);
          if (aLevel !== bLevel) return aLevel - bLevel;
          const aCode = (a.locationCode || a.location_code || a.code || '').toString();
          const bCode = (b.locationCode || b.location_code || b.code || '').toString();
          return aCode.localeCompare(bCode, 'zh-Hant');
        });

        return sortedNodes.map((n: any) => {
          const code = (n.locationCode || n.location_code || n.code || '').toString();
          const name = (n.locationName || n.location_name || n.name || code).toString();
          const display = getInboundLocationDisplayConfig(n);
          const rawChildren = n.children || n.childLocations || n.locations || [];
          const currentCodes = [...parentCodes, name || code];
          const parentPrefix = formatLocationPath(parentCodes);
          const displayLabel = parentPrefix ? `${parentPrefix}-${name || code}` : (name || code);
          const valueKey = (n.id || code || name).toString();
          labelMap[valueKey] = displayLabel;
          const children = buildTree(rawChildren, currentCodes, labelMap);
          const searchText = [name, code, display.label, n.aisle, n.rack, n.shelf, n.bin]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          return {
            title: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                {display.icon}
                <Text strong>{name || code}</Text>
                <Tag color={display.color}>{display.label}</Tag>
                {n.aisle && <Tag color="green">通道: {n.aisle}</Tag>}
                {n.rack && <Tag color="orange">貨架: {n.rack}</Tag>}
                {n.shelf && <Tag color="purple">層: {n.shelf}</Tag>}
                {n.bin && <Tag color="cyan">儲位: {n.bin}</Tag>}
                {n.capacity != null && n.usedCapacity != null && (
                  <Text type="secondary"> {n.usedCapacity}/{n.capacity}</Text>
                )}
              </div>
            ),
            key: (n.id || code || name).toString(),
            value: (n.id || code || name).toString(),
            displayLabel,
            selectable: display.type === 'BIN',
            disabled: display.type !== 'BIN' && children.length === 0,
            searchText,
            children,
          };
        });
      };

      const labelMap: Record<string, string> = {};
      const treeData = buildTree(hierarchy, [], labelMap);
      setLocationTreeData(treeData);
      setLocationLabelMap(labelMap);

      const flatten = (nodes: any[], acc: Location[] = [], whId: string = warehouseId): Location[] => {
        const list = Array.isArray(nodes)
          ? [...nodes].sort((a, b) => {
              const aLevel = Number(a.level ?? 0);
              const bLevel = Number(b.level ?? 0);
              if (aLevel !== bLevel) return aLevel - bLevel;
              const aCode = (a.locationCode || a.location_code || a.code || '').toString();
              const bCode = (b.locationCode || b.location_code || b.code || '').toString();
              return aCode.localeCompare(bCode, 'zh-Hant');
            })
          : [];

        list.forEach((n) => {
          const code = n.locationCode || n.location_code || n.code || '';
          const name = n.locationName || n.location_name || n.name || code;
          acc.push({ id: n.id || code, name, code, warehouseId: whId });
          const childNodes = n.children || n.childLocations || n.locations;
          if (Array.isArray(childNodes) && childNodes.length) flatten(childNodes, acc, whId);
        });
        return acc;
      };
      setWarehouseLocations(flatten(hierarchy));
    } catch (error) {
      console.error('Failed to fetch warehouse locations:', error);
      message.error('載入倉庫位置失敗');
      setWarehouseLocations([]);
      setLocationTreeData([]);
      setLocationLabelMap({});
    } finally {
      setLocationsLoading(false);
    }
  }, [companyIdForInbound]);

  // 產品由後端 API 提供：products 狀態

  const mockBaseUnits = [
    // 單件類
    { value: 'piece', label: '件 (piece/pc)', category: '單件類' },
    
    // 整體設備
    { value: 'unit', label: '台 (unit/set)', category: '整體設備' },
    
    // 包裝類
    { value: 'box', label: '箱 (box)', category: '包裝類' },
    { value: 'pack', label: '包 (pack)', category: '包裝類' },
    { value: 'bag', label: '袋 (bag)', category: '包裝類' },
    { value: 'bundle', label: '束 (bundle)', category: '包裝類' },
    { value: 'cup', label: '杯 (cup)', category: '包裝類' },
    { value: 'can', label: '罐 (can)', category: '包裝類' },
    { value: 'bottle', label: '瓶 (bottle)', category: '包裝類' },
    { value: 'carton', label: '盒 (carton/case)', category: '包裝類' },
    { value: 'roll', label: '卷 (roll)', category: '包裝類' },
    
    // 建材類
    { value: 'block', label: '塊 (block)', category: '建材類' },
    { value: 'sheet', label: '片 (sheet/piece)', category: '建材類' },
    { value: 'rod', label: '根 (rod/bar)', category: '建材類' },
    
    // 文具類
    { value: 'pen', label: '支 (pen/stick)', category: '文具類' },
    { value: 'book', label: '本 (book)', category: '文具類' },
    
    // 衣物類
    { value: 'set', label: '套 (set)', category: '衣物類' },
    
    // 電池類
    { value: 'cell', label: '顆 (cell/unit)', category: '電池類' },
    
    // 電子類
    { value: 'kit', label: '組 (kit/set)', category: '電子類' },
    
    // 食品類
    { value: 'stick', label: '根 (stick)', category: '食品類' },
    { value: 'piece_food', label: '塊 (piece)', category: '食品類' },
    { value: 'bar', label: '條 (bar)', category: '食品類' },
  ];



  // 批量上傳字段配置 - 整合預定入庫表單（13個字段）和產品表單（9個字段）
  const defaultFieldConfig = {
    // === 預定入庫訂單基本信息 (13個字段) ===
    orderNumber: { required: false, label: '入庫單號', description: '系統自動生成，可留空' },
    customer: { required: true, label: '客戶', description: '客戶名稱' },
    warehouse: { required: true, label: '倉庫', description: '倉庫名稱' },
    status: { required: false, label: '狀態', description: '訂單狀態 (upcoming/pending/received等)，可留空默認為upcoming' },
    logisticsMethod: { required: true, label: '物流方法', description: '運輸方式 (如：SF Express、DHL等)' },
    trackingNumber: { required: false, label: '物流追蹤號/參考資料', description: '可選的追蹤號碼或參考信息' },
    expectedArrivalDate: { required: true, label: '預計到達日期', description: 'YYYY-MM-DD格式' },
    shipperContactName: { required: true, label: '貨主聯絡人名', description: '發貨方聯繫人姓名' },
    shipperContactPhone: { required: true, label: '貨主聯絡人電話', description: '發貨方聯繫電話' },
    recipient: { required: false, label: '收件人', description: '自動填充，來自公司設置，可留空' },
    recipientAddress: { required: false, label: '收件地址', description: '自動填充，來自公司設置，可留空' },
    recipientPhone: { required: false, label: '收件電話', description: '自動填充，來自公司設置，可留空' },
    cargoRemark: { required: false, label: '貨物備註', description: '可選的貨物相關備註' },
    
    // === 產品項目詳細信息 (9個字段) ===
    productSku: { required: true, label: '產品SKU', description: '產品唯一識別碼' },
    productName: { required: true, label: '產品名稱', description: '產品完整名稱' },
    lotNumber: { required: false, label: '批次號', description: '產品批次號，用於追蹤' },
    serialNumber: { required: false, label: '序號', description: '產品序列號，用於追蹤' },
    baseUnit: { required: true, label: '基本單位', description: '計量基本單位 (piece/unit/box/pack等)' },
    length: { required: false, label: '長度 (cm)', description: '產品長度，單位厘米' },
    width: { required: false, label: '寬度 (cm)', description: '產品寬度，單位厘米' },
    height: { required: false, label: '高度 (cm)', description: '產品高度，單位厘米' },
    weight: { required: false, label: '重量 (kg)', description: '產品重量，單位公斤' },
    expiryDate: { required: false, label: '到期日期', description: '產品到期日期，YYYY-MM-DD格式' },
    remark: { required: false, label: '產品備註', description: '產品相關備註信息' },
    

  };

  const [fieldConfig, setFieldConfig] = useState(defaultFieldConfig);

  const mockOrders: InboundOrder[] = [
    {
      id: '1',
      orderNumber: 'IN-2024-001',
      customer: 'ABC Company Ltd.',
      customerName: 'ABC Company Ltd.',
      warehouse: 'Main Warehouse',
      warehouseName: 'Main Warehouse',
      status: 'received_quantity_incorrect',
      expectedQuantity: 100,
      confirmedQuantity: 0,
      lotNumber: 'LOT-2024-001',
      serialNumber: 'SN-2024-001',
      dimensions: { length: 50, width: 30, height: 20, unit: 'cm' },
      baseUnit: 'PCS',
      packagingUnit: 'BOX',
      logisticsMethod: 'SF Express',
      trackingNumber: 'SF1234567890',
      referenceNumber: 'REF-2024-001',
      expectedArrivalDate: new Date('2024-01-20'),
      shipperContactName: 'John Smith',
      shipperContactPhone: '+852 1234 5678',
      recipient: 'Shoptoplus',
      recipientAddress: { street: '123 Industrial Ave', city: 'Hong Kong', state: 'HK', postalCode: '000000', country: 'Hong Kong' },
      recipientPhone: '+852 3456 7890',
      cargoRemark: 'Fragile items, handle with care',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-01-15'),
      createdBy: 'admin',
      barcode: 'IN-2024-001',
      qrCode: 'https://shoptoplus.com/inbound/IN-2024-001',
      items: [
        {
          id: '1-1',
          orderId: '1',
          productSku: 'PROD-001',
          productName: 'iPhone 15 Pro',
          lotNumber: 'LOT-2024-001-A',
          serialNumber: 'SN-001-001',
          expiryDate: '2025-12-31',
          warehouse: 'Main Warehouse',
          expectedQuantity: 50,
          confirmedQuantity: 0,
          currentLocation: '',
          currentStock: 0,
          remark: '旗艦產品',
          length: 15,
          width: 7,
          height: 1,
          weight: 0.2,
          baseUnit: 'piece',
          userName: 'admin',
          updateDateTime: new Date().toISOString(),
        },
        {
          id: '1-2',
          orderId: '1',
          productSku: 'PROD-002',
          productName: 'Samsung Galaxy S24',
          lotNumber: 'LOT-2024-001-B',
          serialNumber: 'SN-001-002',
          expiryDate: '2025-06-30',
          warehouse: 'Main Warehouse',
          expectedQuantity: 50,
          confirmedQuantity: 0,
          currentLocation: '',
          currentStock: 0,
          remark: '安卓旗艦',
          length: 16,
          width: 8,
          height: 1,
          weight: 0.25,
          baseUnit: 'piece',
          userName: 'admin',
          updateDateTime: new Date().toISOString(),
        }
      ]
    },
    {
      id: '2',
      orderNumber: 'IN-2024-002',
      customer: 'XYZ Trading Co.',
      customerName: 'XYZ Trading Co.',
      warehouse: 'Secondary Warehouse',
      warehouseName: 'Secondary Warehouse',
      status: 'received_quantity_correct',
      expectedQuantity: 50,
      confirmedQuantity: 50,
      lotNumber: 'LOT-2024-002',
      serialNumber: 'SN-2024-002',
      dimensions: { length: 40, width: 25, height: 15, unit: 'cm' },
      baseUnit: 'PCS',
      packagingUnit: 'CARTON',
      logisticsMethod: 'DHL Express',
      trackingNumber: 'DHL9876543210',
      referenceNumber: 'REF-2024-002',
      expectedArrivalDate: new Date('2024-01-18'),
      receiptDate: new Date('2024-01-18'),
      shipperContactName: 'Mary Wong',
      shipperContactPhone: '+852 2345 6789',
      recipient: 'Shoptoplus',
      recipientAddress: { street: '456 Logistics Rd', city: 'Hong Kong', state: 'HK', postalCode: '000000', country: 'Hong Kong' },
      recipientPhone: '+852 4567 8901',
      cargoRemark: 'Electronics, handle carefully',
      createdAt: new Date('2024-01-12'),
      updatedAt: new Date('2024-01-18'),
      createdBy: 'admin',
      barcode: 'IN-2024-002',
      qrCode: 'https://shoptoplus.com/inbound/IN-2024-002',
      items: [
        {
          id: '2-1',
          orderId: '2',
          productSku: 'PROD-003',
          productName: 'MacBook Pro 14',
          lotNumber: 'LOT-2024-002-A',
          serialNumber: 'SN-002-001',
          expiryDate: '2026-01-15',
          warehouse: 'Secondary Warehouse',
          expectedQuantity: 25,
          confirmedQuantity: 25,
          currentLocation: 'A-01-01',
          currentStock: 25,
          remark: '專業筆電',
          length: 35,
          width: 25,
          height: 2,
          weight: 1.6,
          baseUnit: 'unit',
          userName: 'admin',
          updateDateTime: new Date().toISOString(),
        },
        {
          id: '2-2',
          orderId: '2',
          productSku: 'PROD-004',
          productName: 'AirPods Pro',
          lotNumber: 'LOT-2024-002-B',
          serialNumber: 'SN-002-002',
          expiryDate: '2025-08-30',
          warehouse: 'Secondary Warehouse',
          expectedQuantity: 25,
          confirmedQuantity: 25,
          currentLocation: 'A-01-02',
          currentStock: 25,
          remark: '無線耳機',
          length: 6,
          width: 5,
          height: 3,
          weight: 0.05,
          baseUnit: 'piece',
          userName: 'admin',
          updateDateTime: new Date().toISOString(),
        }
      ]
    },
    // 添加更多模擬數據用於測試分頁
    {
      id: '3',
      orderNumber: 'IN-2024-003',
      customer: 'TechCorp Ltd.',
      customerName: 'TechCorp Ltd.',
      warehouse: 'Main Warehouse',
      warehouseName: 'Main Warehouse',
      status: 'received_quantity_incorrect',
      expectedQuantity: 75,
      confirmedQuantity: 0,
      lotNumber: 'LOT-2024-003',
      serialNumber: 'SN-2024-003',
      dimensions: { length: 30, width: 20, height: 10, unit: 'cm' },
      baseUnit: 'PCS',
      packagingUnit: 'BOX',
      logisticsMethod: 'UPS Express',
      trackingNumber: 'UPS1234567890',
      referenceNumber: 'REF-2024-003',
      expectedArrivalDate: new Date('2024-01-25'),
      shipperContactName: 'Tom Chen',
      shipperContactPhone: '+852 3456 7890',
      recipient: 'Shoptoplus',
      recipientAddress: { street: '789 Tech Ave', city: 'Hong Kong', state: 'HK', postalCode: '000000', country: 'Hong Kong' },
      recipientPhone: '+852 5678 9012',
      cargoRemark: 'Tech equipment, keep dry',
      createdAt: new Date('2024-01-20'),
      updatedAt: new Date('2024-01-20'),
      createdBy: 'admin',
      barcode: 'IN-2024-003',
      qrCode: 'https://shoptoplus.com/inbound/IN-2024-003',
      items: [
        {
          id: '3-1',
          orderId: '3',
          productSku: 'PROD-005',
          productName: 'iPad Air',
          lotNumber: 'LOT-2024-003-A',
          serialNumber: 'SN-003-001',
          expiryDate: '2025-10-31',
          warehouse: 'Main Warehouse',
          expectedQuantity: 75,
          confirmedQuantity: 0,
          currentLocation: '',
          currentStock: 0,
          remark: '平板電腦',
          length: 25,
          width: 18,
          height: 1,
          weight: 0.46,
          baseUnit: 'piece',
          userName: 'admin',
          updateDateTime: new Date().toISOString(),
        }
      ]
    },
    {
      id: '4',
      orderNumber: 'IN-2024-004',
      customer: 'Global Trading Co.',
      customerName: 'Global Trading Co.',
      warehouse: 'Secondary Warehouse',
      warehouseName: 'Secondary Warehouse',
      status: 'received_quantity_incorrect',
      expectedQuantity: 120,
      confirmedQuantity: 80,
      lotNumber: 'LOT-2024-004',
      serialNumber: 'SN-2024-004',
      dimensions: { length: 60, width: 40, height: 30, unit: 'cm' },
      baseUnit: 'PCS',
      packagingUnit: 'CARTON',
      logisticsMethod: 'FedEx Express',
      trackingNumber: 'FX9876543210',
      referenceNumber: 'REF-2024-004',
      expectedArrivalDate: new Date('2024-01-22'),
      receiptDate: new Date('2024-01-23'),
      shipperContactName: 'Lisa Wang',
      shipperContactPhone: '+852 4567 8901',
      recipient: 'Shoptoplus',
      recipientAddress: { street: '456 Global St', city: 'Hong Kong', state: 'HK', postalCode: '000000', country: 'Hong Kong' },
      recipientPhone: '+852 6789 0123',
      cargoRemark: 'Partial delivery accepted',
      createdAt: new Date('2024-01-18'),
      updatedAt: new Date('2024-01-23'),
      createdBy: 'admin',
      barcode: 'IN-2024-004',
      qrCode: 'https://shoptoplus.com/inbound/IN-2024-004',
      items: [
        {
          id: '4-1',
          orderId: '4',
          productSku: 'PROD-006',
          productName: 'Apple Watch Series 9',
          lotNumber: 'LOT-2024-004-A',
          serialNumber: 'SN-004-001',
          expiryDate: '2026-03-31',
          warehouse: 'Secondary Warehouse',
          expectedQuantity: 60,
          confirmedQuantity: 40,
          currentLocation: 'B-02-01',
          currentStock: 40,
          remark: '智能手錶',
          length: 5,
          width: 4,
          height: 1,
          weight: 0.04,
          baseUnit: 'piece',
          userName: 'admin',
          updateDateTime: new Date().toISOString(),
        },
        {
          id: '4-2',
          orderId: '4',
          productSku: 'PROD-007',
          productName: 'Samsung Galaxy Buds',
          lotNumber: 'LOT-2024-004-B',
          serialNumber: 'SN-004-002',
          expiryDate: '2025-12-31',
          warehouse: 'Secondary Warehouse',
          expectedQuantity: 60,
          confirmedQuantity: 40,
          currentLocation: 'B-02-02',
          currentStock: 40,
          remark: '無線耳機',
          length: 6,
          width: 5,
          height: 3,
          weight: 0.06,
          baseUnit: 'piece',
          userName: 'admin',
          updateDateTime: new Date().toISOString(),
        }
      ]
    },
    {
      id: '5',
      orderNumber: 'IN-2024-005',
      customer: 'Fashion House Ltd.',
      customerName: 'Fashion House Ltd.',
      warehouse: 'Main Warehouse',
      warehouseName: 'Main Warehouse',
      status: 'received_quantity_correct',
      expectedQuantity: 200,
      confirmedQuantity: 200,
      lotNumber: 'LOT-2024-005',
      serialNumber: 'SN-2024-005',
      dimensions: { length: 80, width: 60, height: 40, unit: 'cm' },
      baseUnit: 'PCS',
      packagingUnit: 'BOX',
      logisticsMethod: 'TNT Express',
      trackingNumber: 'TNT1122334455',
      referenceNumber: 'REF-2024-005',
      expectedArrivalDate: new Date('2024-01-19'),
      receiptDate: new Date('2024-01-19'),
      shipperContactName: 'Emma Liu',
      shipperContactPhone: '+852 5678 9012',
      recipient: 'Shoptoplus',
      recipientAddress: { street: '123 Fashion Ave', city: 'Hong Kong', state: 'HK', postalCode: '000000', country: 'Hong Kong' },
      recipientPhone: '+852 7890 1234',
      cargoRemark: 'Fashion items, handle with care',
      createdAt: new Date('2024-01-16'),
      updatedAt: new Date('2024-01-19'),
      createdBy: 'admin',
      barcode: 'IN-2024-005',
      qrCode: 'https://shoptoplus.com/inbound/IN-2024-005',
      items: [
        {
          id: '5-1',
          orderId: '5',
          productSku: 'PROD-008',
          productName: 'Nike Air Max',
          lotNumber: 'LOT-2024-005-A',
          serialNumber: 'SN-005-001',
          expiryDate: '2027-01-31',
          warehouse: 'Main Warehouse',
          expectedQuantity: 100,
          confirmedQuantity: 100,
          currentLocation: 'A-03-01',
          currentStock: 100,
          remark: '運動鞋',
          length: 35,
          width: 25,
          height: 15,
          weight: 0.8,
          baseUnit: 'piece',
          userName: 'admin',
          updateDateTime: new Date().toISOString(),
        },
        {
          id: '5-2',
          orderId: '5',
          productSku: 'PROD-009',
          productName: 'Adidas T-Shirt',
          lotNumber: 'LOT-2024-005-B',
          serialNumber: 'SN-005-002',
          expiryDate: '2026-12-31',
          warehouse: 'Main Warehouse',
          expectedQuantity: 100,
          confirmedQuantity: 100,
          currentLocation: 'A-03-02',
          currentStock: 100,
          remark: '運動服裝',
          length: 30,
          width: 25,
          height: 2,
          weight: 0.2,
          baseUnit: 'piece',
          userName: 'admin',
          updateDateTime: new Date().toISOString(),
        }
      ]
    }
  ];

  useEffect(() => {
    loadInboundData();
    // 載入溫度主檔
    apiService.getTemperatureProfiles().then((rows: any) => {
      if (Array.isArray(rows)) setTemperatureOptions(rows as any);
    }).catch(() => {});
  }, []);

  // 載入倉庫清單（與 Outbound 一致，從後端接口）
  useEffect(() => {
    (async () => {
      try {
        const res: any = await apiService.getWarehouses({ limit: 1000 });
        const list: any[] = Array.isArray(res)
          ? res
          : (Array.isArray(res?.data) ? res.data : (Array.isArray(res?.warehouses) ? res.warehouses : []));
        const mapped = list.map((w: any) => ({ id: w.id || w.code || w.warehouseId, name: w.name || w.warehouseName || w.code }));
        setWarehouses(mapped);
      } catch {}
    })();
  }, []);

  // 移除自動搜索，改為只在點擊搜索按鈕時觸發
  useEffect(() => {
    // 只在 orders 變化時更新 filteredOrders，不監聽 searchText
    setFilteredOrders(orders);
  }, [orders]);

  useEffect(() => {
    persistentStoreRef.current = orders;
    PersistentStorage.set('inbound_orders', orders);
  }, [orders]);

  useEffect(() => {
    if (editingItemIndex >= 0 && orderItems[editingItemIndex]) {
      const currentQty = orderItems[editingItemIndex].expectedQuantity || 1;
      setEditingItemExpectedQty(currentQty);
      itemForm.setFieldsValue({ expectedQuantity: currentQty });
    }
  }, [editingItemIndex, orderItems]);

  const loadInboundData = async (page?: number, limit?: number) => {
    setLoading(true);
    try {
      const currentPageToLoad = page || currentPage;
      const currentLimitToLoad = limit || pageSize;
      
      const resp: any = await apiService.getInboundOrders({ 
        page: currentPageToLoad, 
        limit: currentLimitToLoad,
        search: searchText || undefined
      });
      
      const list: InboundOrder[] = (resp?.data || []).map((r: any) => ({
        id: String(r.inboundId ?? r.id ?? r.orderNumber),
        orderNumber: r.orderNumber,
        customer: r.customerName,
        customerName: r.customerName,
        warehouse: r.warehouseName,
        warehouseName: r.warehouseName,
        status: r.status === 'received' ? 'received_quantity_correct' : r.status,
        expectedQuantity: Number(r.expectedQuantity || 0),
        confirmedQuantity: Number(r.confirmedQuantity || 0),
        baseUnit: r.baseUnit || 'piece',
        logisticsMethod: r.logisticsMethod || '',
        trackingNumber: r.trackingNumber || '',
        shipperContactName: r.shipperContactName || '',
        shipperContactPhone: r.shipperContactPhone || '',
        cargoRemark: r.cargoRemark || '',
        expectedArrivalDate: r.expectedArrivalDate ? new Date(r.expectedArrivalDate) : new Date(),
        receiptDate: r.receiptDate ? new Date(r.receiptDate) : undefined,
        createdAt: r.createdAt ? new Date(r.createdAt) : new Date(),
        updatedAt: r.updatedAt ? new Date(r.updatedAt) : new Date(),
        createdBy: 'system',
        items: []
      }));
      
      setOrders(list);
      
      // 處理分頁信息
      if (resp?.pagination) {
        setTotal(resp.pagination.total || 0);
        setCurrentPage(resp.pagination.page || currentPageToLoad);
        setPageSize(resp.pagination.limit || currentLimitToLoad);
      } else {
        // 如果沒有分頁信息，使用數據長度作為總數
        setTotal(list.length);
      }
    } catch (error) {
      message.error('載入入庫訂單失敗');
      setOrders([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const generateOrderNumber = (): string => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `IN-${year}-${month}${day}-${random}`;
  };

  const generateQRCode = async (text: string): Promise<string> => {
    try {
      const dataUrl = await QRCode.toDataURL(text, {
        width: 84,
        margin: 0,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      return dataUrl;
    } catch (error) {
      console.error('QR Code generation failed:', error);
      return '';
    }
  };

  const handleCreateOrder = () => {
    setSelectedOrder(null);
    setOrderItems([]);
    form.resetFields();
    // 自動生成入庫單號
    form.setFieldsValue({
      orderNumber: generateOrderNumber(),
      status: 'new'
    });
    setOrderModalVisible(true);
    
      // 模擬從API獲取產品、客戶和倉庫設置
  // TODO: 這裡將調用實際的API
  // const productData = await fetchProductSettings(selectedProductId);
  // const customerData = await fetchCustomerSettings(selectedCustomerId);
  // const warehouseData = await fetchWarehouseSettings(selectedWarehouseId);
  setTimeout(() => {
    form.setFieldsValue({
      baseUnit: productSettings.baseUnit,
      packagingUnit: productSettings.packagingUnit,
      logisticsMethod: customerSettings.preferredLogistics,
      recipient: warehouseSettings.recipient,
      recipientPhone: warehouseSettings.recipientPhone,
      recipientAddress: warehouseSettings.recipientAddress
    });
  }, 100);
  };

  const handleBulkUpload = () => {
    setBulkUploadModalVisible(true);
  };

  const handleBulkUploadComplete = (newOrders: InboundOrder[]) => {
    // 將新的批量上傳訂單添加到現有訂單列表
    setOrders(prev => [...prev, ...newOrders]);
    setBulkUploadModalVisible(false);
    
    // 保存到本地存儲
    const updatedOrders = [...orders, ...newOrders];
    PersistentStorage.set('inbound_orders', updatedOrders);
    
    message.success(`成功添加 ${newOrders.length} 個入庫訂單`);
  };

  // 導出功能 - 客戶端CSV生成
  const handleExportExcel = () => {
    try {
      // 獲取過濾後的入庫訂單數據
      const filteredOrders = getFilteredOrders();
      
      // 生成CSV內容
      const csvContent = generateCSVContent(filteredOrders);
      
      // 創建Blob並下載
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `inbound_orders_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success('入庫訂單數據已成功導出');
    } catch (error) {
      console.error('導出失敗:', error);
      message.error('導出失敗，請稍後重試');
    }
  };

  // 生成CSV內容
  const generateCSVContent = (data: InboundOrder[]) => {
    const headers = [
      '入庫單號', '客戶', '客戶名稱', '倉庫', '倉庫名稱', '狀態', '預期數量', '確認數量',
      '批次號', '序號', '尺寸', '基本單位', '包裝單位', '物流方式', '追蹤號', '參考號',
      '預期到達日期', '收貨日期', '發貨人聯繫人', '發貨人電話', '收貨人', '收貨地址',
      '收貨人電話', '貨物備註', '創建時間', '更新時間', '創建人', '條形碼', 'QR碼'
    ];

    const rows = data.map(item => [
      item.orderNumber || '',
      item.customer || '',
      item.customerName || '',
      item.warehouse || '',
      item.warehouseName || '',
      item.status || '',
      item.expectedQuantity || '',
      item.confirmedQuantity || '',
      item.lotNumber || '',
      item.serialNumber || '',
      item.dimensions ? `${item.dimensions.length}x${item.dimensions.width}x${item.dimensions.height}${item.dimensions.unit}` : '',
      item.baseUnit || '',
      item.packagingUnit || '',
      item.logisticsMethod || '',
      item.trackingNumber || '',
      item.referenceNumber || '',
      item.expectedArrivalDate ? `\u200B${new Date(item.expectedArrivalDate).toISOString().split('T')[0]}` : '',
      item.receiptDate ? `\u200B${new Date(item.receiptDate).toISOString().split('T')[0]}` : '',
      item.shipperContactName || '',
      item.shipperContactPhone || '',
      item.recipient || '',
      item.recipientAddress ? `${item.recipientAddress.street}, ${item.recipientAddress.city}, ${item.recipientAddress.state} ${item.recipientAddress.postalCode}, ${item.recipientAddress.country}` : '',
      item.recipientPhone || '',
      item.cargoRemark || '',
      item.createdAt ? `\u200B${new Date(item.createdAt).toISOString().split('T')[0]}` : '',
      item.updatedAt ? `\u200B${new Date(item.updatedAt).toISOString().split('T')[0]}` : '',
      item.createdBy || '',
      item.barcode || '',
      item.qrCode || ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  // 獲取過濾後的入庫訂單數據
  const getFilteredOrders = () => {
    // 根據搜索文本過濾
    if (searchText) {
      return filteredOrders.filter(order => 
        order.orderNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
        order.customerName?.toLowerCase().includes(searchText.toLowerCase()) ||
        order.warehouseName?.toLowerCase().includes(searchText.toLowerCase()) ||
        order.trackingNumber?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    return filteredOrders;
  };

  const handleAddItem = () => {
    setEditingItemIndex(-1);
    setSelectedItem(null);
    itemForm.resetFields();
    setEditingItemExpectedQty(1);
    
    // 使用入庫單的倉庫來載入貨位
    // 優先從 selectedOrder 獲取，如果是新建訂單則從表單獲取
    const warehouseList = warehouses.length ? warehouses : mockWarehouses;
    let orderWarehouseId: string | undefined;
    
    // 如果是已保存的訂單，從 selectedOrder 獲取
    if (selectedOrder) {
      const matchedWarehouse = warehouseList.find(
        w => w.id === selectedOrder.warehouse || w.name === selectedOrder.warehouse || 
            w.id === selectedOrder.warehouseName || w.name === selectedOrder.warehouseName
      );
      orderWarehouseId = matchedWarehouse?.id;
    } else {
      // 如果是新建訂單，從表單獲取當前選擇的倉庫
      const formWarehouseValue = form.getFieldValue('warehouse');
      if (formWarehouseValue) {
        const matchedWarehouse = warehouseList.find(
          w => w.id === formWarehouseValue || w.name === formWarehouseValue
        );
        orderWarehouseId = matchedWarehouse?.id || formWarehouseValue;
      }
    }

    if (orderWarehouseId) {
      fetchWarehouseLocations(orderWarehouseId);
    } else {
      setLocationTreeData([]);
    }

    itemForm.setFieldsValue({
      expectedQuantity: 1,
      currentLocation: undefined,
    });
    // Reset products to initial state when opening modal
    loadInitialProducts();
    setItemModalVisible(true);
  };

  const handleEditItem = (index: number) => {
    setEditingItemIndex(index);
    setSelectedItem(orderItems[index]);

    // 準備表單數據，處理日期字段和CBM字段
    const itemToEdit = orderItems[index];
    
    // 使用入庫單的倉庫來載入貨位
    // 優先從 selectedOrder 獲取，如果是新建訂單則從表單獲取
    const warehouseList = warehouses.length ? warehouses : mockWarehouses;
    let orderWarehouseId: string | undefined;
    
    // 如果是已保存的訂單，從 selectedOrder 獲取
    if (selectedOrder) {
      const matchedWarehouse = warehouseList.find(
        w => w.id === selectedOrder.warehouse || w.name === selectedOrder.warehouse || 
            w.id === selectedOrder.warehouseName || w.name === selectedOrder.warehouseName
      );
      orderWarehouseId = matchedWarehouse?.id;
    } else {
      // 如果是新建訂單，從表單獲取當前選擇的倉庫
      const formWarehouseValue = form.getFieldValue('warehouse');
      if (formWarehouseValue) {
        const matchedWarehouse = warehouseList.find(
          w => w.id === formWarehouseValue || w.name === formWarehouseValue
        );
        orderWarehouseId = matchedWarehouse?.id || formWarehouseValue;
      }
    }
    
    if (orderWarehouseId) {
      fetchWarehouseLocations(orderWarehouseId);
    } else {
      setLocationTreeData([]);
    }
    
    const formData = {
      ...itemToEdit,
      expectedQuantity: itemToEdit.expectedQuantity,
      expiryDate: itemToEdit.expiryDate ? dayjs(itemToEdit.expiryDate) : undefined,
      temperatureRequirement: itemToEdit.temperatureRequirement || undefined,
      temperatureId: (itemToEdit as any).temperatureId || undefined,
      packageCBM: (itemToEdit as any).packageCBM != null && (itemToEdit as any).packageCBM !== '' ? Number((itemToEdit as any).packageCBM) : undefined, // 確保CBM為數值
      packagingUnit: itemToEdit.packagingUnit || 'BOX', // 確保包裝單位字段正確填充
      currentLocation: itemToEdit.currentLocation || undefined,
    };

    console.log('編輯產品:', itemToEdit); // 調試信息
    console.log('表單數據:', formData); // 調試信息

    itemForm.setFieldsValue(formData);
    // Reset products to initial state when opening modal
    loadInitialProducts();
    setItemModalVisible(true);
  };

  const handleRemoveItem = async (index: number) => {
    try {
      const item = orderItems[index];
      const orderId = selectedOrder?.id;
      if (orderId && item?.id) {
        await apiService.deleteInboundOrderItem(orderId, String(item.id));
      }
      const newItems = orderItems.filter((_, i) => i !== index);
      setOrderItems(newItems);
      message.success('產品已移除');
    } catch (e) {
      message.error('刪除產品失敗');
    }
  };

  // 新增：更新产品预报数量的函数
  const handleUpdateItemQuantity = (index: number, quantity: number) => {
    const safeQty = quantity > 0 ? quantity : 1;
      const newItems = [...orderItems];
      newItems[index] = {
        ...newItems[index],
        expectedQuantity: safeQty
      };
      setOrderItems(newItems);
      if (selectedOrder) {
        setOrders(prev => prev.map(order => order.id === selectedOrder.id ? { ...order, items: newItems } : order));
      }
  };

  const handleSubmitItem = async (values: any) => {
    const effectiveExpectedQty = editingItemIndex >= 0 ? editingItemExpectedQty : (values.expectedQuantity || 1);
    const newItem: InboundItem = {
      id: editingItemIndex >= 0 ? selectedItem?.id || Date.now().toString() : Date.now().toString(),
      orderId: selectedOrder?.id || '',
      productSku: values.productSku,
      productName: values.productName,
      lotNumber: values.lotNumber || '',
      serialNumber: values.serialNumber || '',
      expiryDate: values.expiryDate?.format('YYYY-MM-DD'),
      temperatureRequirement: values.temperatureRequirement,
      temperatureId: values.temperatureId,
      warehouse: selectedOrder?.warehouse || selectedOrder?.warehouseName || '', // 使用入庫單的倉庫
      expectedQuantity: effectiveExpectedQty,
      confirmedQuantity: editingItemIndex >= 0 ? selectedItem?.confirmedQuantity || 0 : 0,
      currentLocation: values.currentLocation || '',
      currentStock: editingItemIndex >= 0 ? selectedItem?.currentStock || 0 : 0,
      remark: values.remark || '',
      length: values.length || 0,
      width: values.width || 0,
      height: values.height || 0,
      weight: values.weight || 0,
      packageCBM: values.packageCBM != null && values.packageCBM !== '' ? Number(values.packageCBM) : undefined, // 確保為數值
      packagingUnit: values.packagingUnit || 'BOX', // 新增 包裝單位 字段
      baseUnit: values.baseUnit, // 新增 baseUnit 字段
      userName: editingItemIndex >= 0 ? selectedItem?.userName || 'admin' : 'admin',
      updateDateTime: new Date().toISOString(),
    };

    try {
      let savedItem = newItem;
      if (selectedOrder) {
        const resp: any = await apiService.upsertInboundOrderItem(selectedOrder.id, newItem);
        if (resp?.data) {
          savedItem = {
            ...newItem,
            ...resp.data,
          } as any;
        }
      }
      if (editingItemIndex >= 0) {
        const newItems = [...orderItems];
        newItems[editingItemIndex] = savedItem;
        setOrderItems(newItems);
    if (selectedOrder) {
      setOrders(prev => prev.map(order => order.id === selectedOrder.id ? { ...order, items: newItems } : order));
    }
        message.success('產品已更新');
      } else {
        const newItems = [...orderItems, savedItem];
        setOrderItems(newItems);
        if (selectedOrder) {
          setOrders(prev => prev.map(order => order.id === selectedOrder.id ? { ...order, items: newItems } : order));
        }
        message.success('產品已添加');
      }
    } catch (e) {
      message.error('保存產品到伺服器失敗');
    }

    setItemModalVisible(false);
    setEditingItemIndex(-1);
    setSelectedItem(null);
    itemForm.resetFields();
    setEditingItemExpectedQty(1);
    // 重新拉取後端最新的產品列表，確保回顯
    if (selectedOrder) {
      try {
        const resp: any = await apiService.getInboundOrderItems(selectedOrder.id);
        const items = resp?.data || [];
        if (Array.isArray(items)) setOrderItems(items as any);
      } catch {}
    }
  };

  const handleEditOrder = (order: InboundOrder) => {
    setIsReadOnlyMode(false);
    setSelectedOrder(order);
    setOrderItems(order.items || []);
    // 從後端拉取最新的訂單產品
    apiService.getInboundOrderItems(order.id).then((resp: any) => {
      const items = resp?.data || [];
      if (items.length) {
        setOrderItems(items as any);
      }
    }).catch(() => {});
    
    // Ensure expectedArrivalDate is a Date object for dayjs conversion
    const expectedArrivalDate = order.expectedArrivalDate instanceof Date 
      ? order.expectedArrivalDate 
      : new Date(order.expectedArrivalDate);
    
    form.setFieldsValue({
      orderNumber: order.orderNumber,
      customer: order.customer,
      warehouse: order.warehouse,
      status: order.status,
      expectedQuantity: order.expectedQuantity,
      logisticsMethod: order.logisticsMethod,
      trackingNumber: order.trackingNumber,
      expectedArrivalDate: dayjs(expectedArrivalDate),
      shipperContactName: order.shipperContactName,
      shipperContactPhone: order.shipperContactPhone,
      recipient: warehouseSettings.recipient || 'Shoptoplus',
      recipientAddress: warehouseSettings.recipientAddress || '荃灣 柴灣角街66-82號 金熊工業中心C座18樓',
      recipientPhone: warehouseSettings.recipientPhone || '21568011',
      cargoRemark: order.cargoRemark,
    });
    setOrderModalVisible(true);
  };

  const handleViewOrder = async (order: InboundOrder) => {
    setIsReadOnlyMode(true);
    let detail: InboundOrder | null = null;
    try {
      const resp: any = await apiService.getInboundOrderById(order.id);
      const mapped: InboundOrder = {
        id: String(resp.inboundId ?? order.id),
        orderNumber: resp.orderNumber ?? order.orderNumber,
        customer: resp.customerName ?? order.customerName,
        customerName: resp.customerName ?? order.customerName,
        warehouse: resp.warehouseName ?? order.warehouseName,
        warehouseName: resp.warehouseName ?? order.warehouseName,
        status: resp.status ?? order.status,
        expectedQuantity: Number(resp.expectedQuantity ?? order.expectedQuantity ?? 0),
        confirmedQuantity: Number(resp.confirmedQuantity ?? order.confirmedQuantity ?? 0),
        baseUnit: resp.baseUnit ?? order.baseUnit,
        packagingUnit: (resp as any).packagingUnit ?? (order as any).packagingUnit ?? 'BOX',
        logisticsMethod: resp.logisticsMethod ?? order.logisticsMethod,
        trackingNumber: resp.trackingNumber ?? order.trackingNumber,
        shipperContactName: resp.shipperContactName ?? order.shipperContactName,
        shipperContactPhone: resp.shipperContactPhone ?? order.shipperContactPhone,
        cargoRemark: resp.cargoRemark ?? order.cargoRemark,
        expectedArrivalDate: resp.expectedArrivalDate ? new Date(resp.expectedArrivalDate) : order.expectedArrivalDate,
        receiptDate: resp.receiptDate ? new Date(resp.receiptDate) : order.receiptDate,
        createdAt: resp.createdAt ? new Date(resp.createdAt) : order.createdAt,
        updatedAt: resp.updatedAt ? new Date(resp.updatedAt) : order.updatedAt,
        createdBy: order.createdBy || 'system',
        items: order.items || []
      };
      detail = mapped;
    } catch (e) {
      detail = order;
    } finally {
      const payload = detail as InboundOrder;
      setSelectedOrder(payload);
      // 拉取訂單產品，優先使用後端結果
      try {
        const respItems: any = await apiService.getInboundOrderItems(payload.id);
        const items = respItems?.data || [];
      const normalizedItems = Array.isArray(items) ? (items as any) : (payload.items || []);
      setOrderItems(normalizedItems);
      setOrders(prev => prev.map(order => order.id === payload.id ? { ...order, items: normalizedItems } : order));
      } catch {
      const fallbackItems = payload.items || [];
      setOrderItems(fallbackItems);
      setOrders(prev => prev.map(order => order.id === payload.id ? { ...order, items: fallbackItems } : order));
      }
      try {
        const expectedArrivalDate = payload?.expectedArrivalDate instanceof Date 
          ? payload?.expectedArrivalDate 
          : (payload?.expectedArrivalDate ? new Date(payload?.expectedArrivalDate) : undefined);
        form.setFieldsValue({
          orderNumber: payload?.orderNumber,
          customer: payload?.customer,
          warehouse: payload?.warehouse,
          status: payload?.status,
          expectedQuantity: payload?.expectedQuantity,
          logisticsMethod: payload?.logisticsMethod,
          trackingNumber: (payload as any)?.trackingNumber,
          expectedArrivalDate: expectedArrivalDate ? dayjs(expectedArrivalDate) : undefined,
          shipperContactName: payload?.shipperContactName,
          shipperContactPhone: payload?.shipperContactPhone,
          recipient: warehouseSettings.recipient || 'Shoptoplus',
          recipientAddress: warehouseSettings.recipientAddress || '荃灣 柴灣角街66-82號 金熊工業中心C座18樓',
          recipientPhone: warehouseSettings.recipientPhone || '21568011',
          cargoRemark: payload?.cargoRemark,
        });
      } catch {}
      setOrderModalVisible(true);
      // 舊的單獨詳情模態關閉
      setViewModalVisible(false);
    }
  };

  const handleReceiveOrder = async (orderId: string) => {
    try {
      // 找到對應的入庫訂單
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        message.error('找不到對應的入庫訂單');
        return;
      }

      setSelectedReceiptOrder(order);

      // 【修復】從後端拉取最新的訂單產品項目
      let itemsToLoad: InboundItem[] = [];
      try {
        const resp: any = await apiService.getInboundOrderItems(order.id);
        const fetchedItems = resp?.data || [];
        if (Array.isArray(fetchedItems) && fetchedItems.length > 0) {
          itemsToLoad = fetchedItems;
        } else {
          // 如果後端沒有產品項目，使用本地緩存
          itemsToLoad = order.items || [];
        }
      } catch (error) {
        console.warn('無法從後端獲取產品項目，使用本地數據:', error);
        itemsToLoad = order.items || [];
      }

      // 初始化收货项目，确保确认数量有正确的默认值
      const initializedItems = itemsToLoad.map(item => ({
        ...item,
        confirmedQuantity: item.confirmedQuantity || item.expectedQuantity
      }));

      setReceiptItems(initializedItems);

      // Find warehouse ID from order.warehouse (which might be name or ID)
      const warehouseList = warehouses.length ? warehouses : mockWarehouses;
      const matchedWarehouse = warehouseList.find(
        w => w.id === order.warehouse || w.name === order.warehouse
      );
      const warehouseIdToUse = matchedWarehouse?.id || order.warehouse;

      // 預填充表單數據
      receiptForm.setFieldsValue({
        orderNumber: order.orderNumber,
        customer: order.customerName,
        warehouse: warehouseIdToUse,
        orderDate: dayjs(order.createdAt),
        receiptDate: dayjs(new Date()),
        status: 'received_quantity_correct'
      });

      // Fetch warehouse locations for the selected warehouse
      if (warehouseIdToUse) {
        await fetchWarehouseLocations(warehouseIdToUse);
      }

      setReceiptModalVisible(true);
    } catch (error) {
      console.error('打開收貨表單失敗:', error);
      message.error('打開收貨表單失敗');
    }
  };







  const handleDownloadTemplate = () => {
    // 根據新的23個字段配置生成完整模板
    const allFieldLabels = Object.entries(fieldConfig).map(([_, config]) => config.label);
    
    // 示例數據行1 - 完整的正確數據
    const exampleRow1 = [
      '', // 入庫單號 - 系統生成
      'ABC Company Ltd.', // 客戶
      'Main Warehouse', // 倉庫
      'upcoming', // 狀態
      '100', // 預報數量
      'SF Express', // 物流方法
      'SF1234567890', // 物流追蹤號/參考資料
      '2024-01-25', // 預計到達日期
      'John Smith', // 貨主聯絡人名
      '+852 1234 5678', // 貨主聯絡人電話
      '', // 收件人 - 自動填充
      '', // 收件地址 - 自動填充
      '', // 收件電話 - 自動填充
      'Fragile electronic items', // 貨物備註
      'PROD-001', // 產品SKU
      'iPhone 15 Pro', // 產品名稱
      'LOT-2024-001-A', // 批次號
      'SN-001-001', // 序號
      'piece', // 基本單位
      '15.0', // 長度 (cm)
      '7.0', // 寬度 (cm)
      '1.0', // 高度 (cm)
      '0.2', // 重量 (kg)
      '2025-12-31', // 到期日期
      '旗艦手機產品' // 產品備註
    ];
    
    // 示例數據行2 - 另一個產品示例
    const exampleRow2 = [
      '', 
      'XYZ Trading Co.', 
      'Secondary Warehouse', 
      'pending', 
      '50', 
      'DHL Express', 
      'DHL9876543210', 
      '2024-01-28', 
      'Mary Wong', 
      '+852 2345 6789', 
      '', 
      '', 
      '', 
      'Professional laptop computers',
      'PROD-003', 
      'MacBook Pro 14',
      'LOT-2024-002-A', 
      'SN-002-001', 
      'unit', 
      '35.0', 
      '25.0', 
      '2.0', 
      '1.6', 
      '2026-01-15', 
      '專業筆記本電腦'
    ];

    const template = [
      allFieldLabels, // 標題行
      exampleRow1,    // 示例數據1
      exampleRow2     // 示例數據2
    ];

    // 生成CSV內容，使用UTF-8 BOM確保中文正確顯示
    const csvContent = '\uFEFF' + template.map(row => 
      row.map(cell => `"${cell.toString().replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inbound_order_template_v2.csv';
    a.click();
    window.URL.revokeObjectURL(url);

    message.success('模板已下載！包含23個字段的完整入庫訂單模板。');
  };

  const handleFieldConfig = () => {
    // 將當前配置轉換為表單格式
    const formValues = Object.entries(fieldConfig).reduce((acc, [key, config]) => {
      acc[key] = config.required;
      return acc;
    }, {} as any);
    
    fieldConfigForm.setFieldsValue(formValues);
    setFieldConfigModalVisible(true);
  };

  const handleSaveFieldConfig = (values: any) => {
    const newConfig = Object.entries(values).reduce((acc, [key, required]) => {
      (acc as any)[key] = {
        ...(fieldConfig as any)[key],
        required: required as boolean
      };
      return acc;
    }, {} as typeof fieldConfig);
    
    setFieldConfig(newConfig);
    setFieldConfigModalVisible(false);
    message.success('字段配置已保存');
  };

  const handlePrintOrder = async (order: InboundOrder) => {
    // 使用pdfUtils中的专用函数 - 生成入库单（订单概览）
    await generateInboundVoucherPDF(order.orderNumber);
  };


  // 收貨相關函數 - 添加數量差異檢查狀態
  const [quantityDifferenceFields, setQuantityDifferenceFields] = useState<Set<string>>(new Set());

  // 檢查並更新數量差異狀態
  const checkQuantityDifference = (expectedQuantity: number, confirmedQuantity: number, fieldKey: string) => {
    const newDifferenceFields = new Set(quantityDifferenceFields);
    if (expectedQuantity !== confirmedQuantity) {
      newDifferenceFields.add(fieldKey);
    } else {
      newDifferenceFields.delete(fieldKey);
    }
    setQuantityDifferenceFields(newDifferenceFields);
  };

  const handleAddReceiptItem = () => {
    setEditingReceiptItemIndex(-1);
    setSelectedReceiptItem(null);
    receiptItemForm.resetFields();

    // 重置數量差異狀態
    setQuantityDifferenceFields(new Set());

    // 使用入庫單的倉庫來載入貨位
    const warehouseList = warehouses.length ? warehouses : mockWarehouses;
    let orderWarehouseId: string | undefined;
    if (selectedReceiptOrder) {
      const matchedWarehouse = warehouseList.find(
        w => w.id === selectedReceiptOrder.warehouse || w.name === selectedReceiptOrder.warehouse || 
            w.id === selectedReceiptOrder.warehouseName || w.name === selectedReceiptOrder.warehouseName
      );
      orderWarehouseId = matchedWarehouse?.id;
    }
    
    if (orderWarehouseId) {
      fetchWarehouseLocations(orderWarehouseId);
    } else {
      setLocationTreeData([]);
    }

    // 為新產品自動填充系統字段
    receiptItemForm.setFieldsValue({
      userName: 'admin',
      updateDateTime: new Date().toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    });

    // Reset products to initial state when opening modal
    loadInitialProducts();
    setReceiptItemModalVisible(true);
  };

  const handleEditReceiptItem = (index: number) => {
    setEditingReceiptItemIndex(index);
    setSelectedReceiptItem(receiptItems[index]);

    // 準備表單數據，處理日期字段
    const itemToEdit = receiptItems[index];
    
    // 使用入庫單的倉庫來載入貨位
    const warehouseList = warehouses.length ? warehouses : mockWarehouses;
    let orderWarehouseId: string | undefined;
    if (selectedReceiptOrder) {
      const matchedWarehouse = warehouseList.find(
        w => w.id === selectedReceiptOrder.warehouse || w.name === selectedReceiptOrder.warehouse || 
            w.id === selectedReceiptOrder.warehouseName || w.name === selectedReceiptOrder.warehouseName
      );
      orderWarehouseId = matchedWarehouse?.id;
    }
    
    if (orderWarehouseId) {
      fetchWarehouseLocations(orderWarehouseId);
    } else {
      setLocationTreeData([]);
    }
    
    const formData = {
      ...itemToEdit,
      expiryDate: itemToEdit.expiryDate ? dayjs(itemToEdit.expiryDate) : undefined,
      // 系統字段自動填充
      userName: itemToEdit.userName || 'admin',
      updateDateTime: new Date().toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    };

    console.log('編輯收貨產品:', itemToEdit); // 調試信息
    console.log('收貨表單數據:', formData); // 調試信息

    receiptItemForm.setFieldsValue(formData);

    // 重置數量差異狀態並檢查當前數值
    setQuantityDifferenceFields(new Set());
    setTimeout(() => {
      if (itemToEdit.expectedQuantity !== itemToEdit.confirmedQuantity) {
        checkQuantityDifference(itemToEdit.expectedQuantity, itemToEdit.confirmedQuantity, 'confirmedQuantity');
      }
    }, 100);

    // Reset products to initial state when opening modal
    loadInitialProducts();
    setReceiptItemModalVisible(true);
  };

  const handleRemoveReceiptItem = (index: number) => {
    const newItems = receiptItems.filter((_, i) => i !== index);
    setReceiptItems(newItems);
    message.success('產品已移除');
  };

  // 新增：更新收货产品数量的函数（兩者不聯動，僅更新對應字段）
  const handleUpdateReceiptItemQuantity = (index: number, field: 'expectedQuantity' | 'confirmedQuantity', quantity: number) => {
    const newItems = [...receiptItems];
    const updatedItem = {
      ...newItems[index],
      [field]: quantity || 0,
    } as InboundItem;
    
    newItems[index] = updatedItem;
    setReceiptItems(newItems);
    
    // 更新数量差异状态
    if (field === 'confirmedQuantity') {
      checkQuantityDifference(updatedItem.expectedQuantity, updatedItem.confirmedQuantity, 'confirmedQuantity');
    }
  };

  const handleSubmitReceiptItem = (values: any) => {
    const newItem: InboundItem = {
      id: editingReceiptItemIndex >= 0 ? selectedReceiptItem?.id || Date.now().toString() : Date.now().toString(),
      orderId: selectedReceiptOrder?.id || '',
      productSku: values.productSku,
      productName: values.productName,
      lotNumber: values.lotNumber || '',
      serialNumber: values.serialNumber || '',
      expiryDate: values.expiryDate?.format('YYYY-MM-DD'),
      warehouse: values.warehouse,
      // 數量：確認數量可以與預報數量不同
      expectedQuantity: editingReceiptItemIndex >= 0 ? (selectedReceiptItem?.expectedQuantity || 1) : 1,
      confirmedQuantity: editingReceiptItemIndex >= 0 ? (selectedReceiptItem?.confirmedQuantity || selectedReceiptItem?.expectedQuantity || 1) : (values.confirmedQuantity || 1),
      currentLocation: values.currentLocation || '',
      currentStock: values.currentStock || 0,
      remark: values.remark || '',
      length: values.length || 0,
      width: values.width || 0,
      height: values.height || 0,
      weight: values.weight || 0,
      baseUnit: values.baseUnit || 'piece', // 新增 baseUnit 字段，默認為'piece'
      userName: values.userName || 'admin', // 從表單獲取，系統自動填充
      updateDateTime: new Date().toISOString(), // 服務器端會更新，這裡只是本地顯示
    };

    if (editingReceiptItemIndex >= 0) {
      // Edit existing item
      const newItems = [...receiptItems];
      newItems[editingReceiptItemIndex] = newItem;
      setReceiptItems(newItems);
      message.success('產品已更新');
    } else {
      // Add new item
      setReceiptItems([...receiptItems, newItem]);
      message.success('產品已添加');
    }

    setReceiptItemModalVisible(false);
    setEditingReceiptItemIndex(-1);
    setSelectedReceiptItem(null);
    receiptItemForm.resetFields();
    // 重置數量差異狀態
    setQuantityDifferenceFields(new Set());
  };

  const handleSubmitReceipt = async (values: any) => {
    try {
      if (!selectedReceiptOrder) return;

      // 檢查數量差異，自動設置狀態
      const hasQuantityDifference = receiptItems.some(item => item.expectedQuantity !== item.confirmedQuantity);
      const autoStatus = hasQuantityDifference ? 'received_quantity_incorrect' : 'received_quantity_correct';

      // 計算總確認數量
      const totalConfirmedQuantity = receiptItems.reduce((sum, item) => sum + (Number(item.confirmedQuantity) || 0), 0);

      // 【修復】保存到數據庫 - 更新所有產品項目 (MUST happen FIRST before status update)
      for (const item of receiptItems) {
        await apiService.upsertInboundOrderItem(selectedReceiptOrder.id, {
          id: item.id,
          productSku: item.productSku,
          productName: item.productName,
          lotNumber: item.lotNumber,
          expiryDate: item.expiryDate,
          expectedQuantity: item.expectedQuantity,
          confirmedQuantity: item.confirmedQuantity,
          baseUnit: item.baseUnit,
          packagingUnit: item.packagingUnit,
          currentLocation: item.currentLocation,
          currentStock: item.confirmedQuantity, // 當前庫存更新為確認數量
          remark: item.remark,
        });
      }

      // 【修復】保存到數據庫 - 更新入庫訂單 (MUST happen AFTER items are saved)
      // This triggers inventory creation, so items must exist first
      await apiService.updateInboundOrder(selectedReceiptOrder.id, {
        status: autoStatus,
        confirmedQuantity: totalConfirmedQuantity,
        receiptDate: values.receiptDate?.toDate(),
      });

      // 更新入庫訂單本地狀態
      const updatedOrder: InboundOrder = {
        ...selectedReceiptOrder,
        status: autoStatus,
        confirmedQuantity: totalConfirmedQuantity,
        receiptDate: values.receiptDate?.toDate(),
        items: receiptItems,
        updatedAt: new Date(),
      };

      // 更新訂單列表
      const updatedOrders = orders.map(order =>
        order.id === selectedReceiptOrder.id ? updatedOrder : order
      );

      setOrders(updatedOrders);
      PersistentStorage.set('inboundOrders', updatedOrders);

      message.success('收貨單保存成功');
      setReceiptModalVisible(false);
      setSelectedReceiptOrder(null);
      setReceiptItems([]);
      receiptForm.resetFields();
    } catch (error) {
      console.error('保存收貨單失敗:', error);
      message.error('保存收貨單失敗，請檢查網絡連接');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'blue';
      case 'received_quantity_correct': return 'green';
      case 'received_quantity_incorrect': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new': return 'New';
      case 'received_quantity_correct': return '已收貨 數量正確';
      case 'received_quantity_incorrect': return '已收貨 數量不正確';
      default: return status;
    }
  };

  // 處理列點擊排序
  const handleColumnSort = (field: string) => {
    if (sortField === field) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortField(null);
        setSortOrder(null);
      }
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // 獲取排序圖標
  const getSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // 定義所有可用的列
  const allColumns = {
    orderNumber: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('orderNumber')}
        >
          入庫單號
          {getSortIcon('orderNumber')}
        </div>
      ),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      sorter: true,
      render: (text: string) => <Text code>{text}</Text>,
    },
    customerName: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('customerName')}
        >
          客戶
          {getSortIcon('customerName')}
        </div>
      ),
      dataIndex: 'customerName',
      key: 'customerName',
      sorter: true,
    },
    warehouseName: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('warehouseName')}
        >
          倉庫
          {getSortIcon('warehouseName')}
        </div>
      ),
      dataIndex: 'warehouseName',
      key: 'warehouseName',
      sorter: true,
    },
    status: {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = {
          new: { color: 'blue', text: 'New', icon: <ClockCircleOutlined /> },
          received_quantity_correct: { color: 'green', text: '已收貨 數量正確', icon: <CheckCircleOutlined /> },
          received_quantity_incorrect: { color: 'red', text: '已收貨 數量不正確', icon: <ExclamationCircleOutlined /> },
        } as const;
        const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: '-', icon: null } as any;
        return <Tag color={(config as any).color} icon={(config as any).icon}>{(config as any).text}</Tag>;
      },
    },
    expectedQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('expectedQuantity')}
        >
          預期數量
          {getSortIcon('expectedQuantity')}
        </div>
      ),
      dataIndex: 'expectedQuantity',
      key: 'expectedQuantity',
      sorter: true,
      render: (quantity: number, record: InboundOrder) => {
        // 優先使用產品明細的總和，如果沒有明細則使用訂單主表的數量
        const totalQuantity = record.items?.length 
          ? record.items.reduce((sum, item) => sum + (Number(item.expectedQuantity) || 0), 0)
          : (Number(quantity) || 0);
        const itemCount = record.items?.length || 0;
        return (
          <div>
            <div>{totalQuantity} {record.baseUnit}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.packagingUnit} {itemCount > 0 && `(${itemCount} 項產品)`}
            </Text>
          </div>
        );
      },
    },
    confirmedQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('confirmedQuantity')}
        >
          收貨數量
          {getSortIcon('confirmedQuantity')}
        </div>
      ),
      dataIndex: 'confirmedQuantity',
      key: 'confirmedQuantity',
      sorter: true,
      render: (quantity: number, record: InboundOrder) => {
        const totalConfirmed = record.items?.reduce((sum, item) => sum + (Number(item.confirmedQuantity) || 0), 0) || quantity || 0;
        const totalExpected = record.items?.reduce((sum, item) => sum + (Number(item.expectedQuantity) || 0), 0) || record.expectedQuantity || 0;
        const difference = totalConfirmed - totalExpected;
        return (
          <div>
            <div>
              {totalConfirmed} {record.baseUnit}
            </div>
            <Text type={difference === 0 ? 'secondary' : difference > 0 ? 'success' : 'danger'} style={{ fontSize: '12px' }}>
              {difference === 0 ? '與預期相同' : difference > 0 ? `多收 ${difference}` : `少收 ${Math.abs(difference)}`}
            </Text>
          </div>
        );
      },
    },
    logisticsMethod: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('logisticsMethod')}
        >
          物流方法
          {getSortIcon('logisticsMethod')}
        </div>
      ),
      dataIndex: 'logisticsMethod',
      key: 'logisticsMethod',
      sorter: true,
      render: (method: string) => method || <Text type="secondary">-</Text>,
    },
    expectedArrivalDate: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleColumnSort('expectedArrivalDate')}
        >
          預期到達
          {getSortIcon('expectedArrivalDate')}
        </div>
      ),
      dataIndex: 'expectedArrivalDate',
      key: 'expectedArrivalDate',
      sorter: true,
      render: (date: Date | string) => {
        if (date instanceof Date) {
          return date.toLocaleDateString();
        } else if (typeof date === 'string') {
          return new Date(date).toLocaleDateString();
        }
        return '';
      },
    },
    trackingNumber: {
      title: '物流追蹤號/參考資料',
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
      width: 160,
      render: (text: string) => text ? <Text code>{text}</Text> : <Text type="secondary">-</Text>,
    },
    shipperContactName: {
      title: '貨主聯絡人名',
      dataIndex: 'shipperContactName',
      key: 'shipperContactName',
      width: 120,
      render: (text: string) => text || <Text type="secondary">-</Text>,
    },
    shipperContactPhone: {
      title: '貨主聯絡人電話',
      dataIndex: 'shipperContactPhone',
      key: 'shipperContactPhone',
      width: 130,
      render: (text: string) => text || <Text type="secondary">-</Text>,
    },
    recipient: {
      title: '收件人',
      dataIndex: 'recipient',
      key: 'recipient',
      width: 120,
      render: (text: string) => text || <Text type="secondary">-</Text>,
    },
    recipientAddress: {
      title: '收件地址',
      key: 'recipientAddress',
      width: 200,
      render: (record: InboundOrder) => {
        if (!record.recipientAddress) return <Text type="secondary">-</Text>;
        const address = typeof record.recipientAddress === 'string' 
          ? record.recipientAddress 
          : record.recipientAddress.street;
        return (
          <Tooltip title={address}>
            <Text ellipsis style={{ maxWidth: 180 }}>
              {address.length > 30 ? `${address.substring(0, 30)}...` : address}
            </Text>
          </Tooltip>
        );
      },
    },
    recipientPhone: {
      title: '收件電話',
      dataIndex: 'recipientPhone',
      key: 'recipientPhone',
      width: 130,
      render: (text: string) => text || <Text type="secondary">-</Text>,
    },
    cargoRemark: {
      title: '貨物備註',
      dataIndex: 'cargoRemark',
      key: 'cargoRemark',
      width: 150,
      render: (text: string) => {
        if (!text) return <Text type="secondary">-</Text>;
        return (
          <Tooltip title={text}>
            <Text ellipsis style={{ maxWidth: 120 }}>
              {text.length > 20 ? `${text.substring(0, 20)}...` : text}
            </Text>
          </Tooltip>
        );
      },
    },
    actions: {
      title: '操作',
      key: 'actions',
      fixed: 'right' as const,
      width: 340,
      render: (record: InboundOrder) => (
        <Space>
          <Tooltip title="查看詳情">
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewOrder(record)}
            >
              詳情
            </Button>
          </Tooltip>
          <Tooltip title="列印">
            <Button
              type="primary"
              size="small"
              icon={<PrinterOutlined />}
              onClick={() => handlePrintOrder(record)}
            >
              列印
            </Button>
          </Tooltip>
          <Tooltip title="打印入庫憑證">
            <Button
              type="default"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={async () => await generateInboundBeePDF(record.orderNumber)}
            >
              入庫憑證
            </Button>
          </Tooltip>
          <Tooltip title="編輯">
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditOrder(record)}
            >
              編輯
            </Button>
          </Tooltip>
          {/* 收貨按鈕邏輯：只有在有產品數據且尚未完全收貨完成時才顯示收貨按鈕 */}
          {shouldShowReceiptButton(record) ? (
            <Button
              size="small"
              type="primary"
              onClick={() => handleReceiveOrder(record.id)}
            >
              收貨
            </Button>
          ) : null}
        </Space>
      ),
    },
  };

  // 檢查入庫單是否已完全收貨完成
  const isOrderFullyReceived = (order: InboundOrder): boolean => {
    if (!order.items || order.items.length === 0) {
      // 如果沒有產品項目，根據狀態判斷
      return order.status === 'received_quantity_correct' || order.status === 'received_quantity_incorrect';
    }
    
    // 檢查所有產品項目是否都已完全收貨
    return order.items.every(item => item.confirmedQuantity >= item.expectedQuantity);
  };

  // 檢查入庫單是否應該顯示收貨按鈕
  const shouldShowReceiptButton = (order: InboundOrder): boolean => {
    // 如果訂單狀態已經是已收貨狀態，則不顯示收貨按鈕
    if (order.status === 'received_quantity_correct' || order.status === 'received_quantity_incorrect') {
      return false;
    }
    
    // 如果有產品項目數據，檢查是否已完全收貨
    if (order.items && order.items.length > 0) {
      if (isOrderFullyReceived(order)) {
        return false;
      }
    }
    
    // 其他情況顯示收貨按鈕（包括items為空的新訂單）
    return true;
  };

  // 檢查入庫單是否有數量異常
  const hasQuantityAnomaly = (order: InboundOrder): boolean => {
    // 1) 以狀態為最高優先：只有「已收貨 數量不正確」標紅
    if (order.status === 'received_quantity_incorrect') return true;
    if (order.status === 'received_quantity_correct') return false;

    // 2) 回退邏輯：逐項比對（數值化避免字串比較誤判）
    if (!order.items || order.items.length === 0) {
      return false;
    }
    return order.items.some(item => Number(item.confirmedQuantity) !== Number(item.expectedQuantity));
  };

  // 根據列配置生成顯示的列
  const columns: ColumnsType<InboundOrder> = Object.entries(columnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allColumns[key as keyof typeof allColumns];
      if (!column) {
        console.warn(`Missing column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean); // 過濾掉undefined的列

  // 使入庫單表支持拖拽列排序（排除 操作 列）
  const { columns: draggableColumns, order: inboundOrder } = useDraggableColumns<InboundOrder>({
    columns: columns as any,
    disableDragKeys: ['actions'],
    storageKey: 'inbound-order-table',
  });

  // 拖拽後同步回列配置的 order
  React.useEffect(() => {
    if (!inboundOrder || inboundOrder.length === 0) return;
    setColumnSettings((prev: any) => {
      const next: any = { ...prev };
      inboundOrder.forEach((k: string, idx: number) => {
        if (next[k]) next[k] = { ...next[k], order: idx };
      });
      Object.keys(next).forEach((k) => {
        if (!inboundOrder.includes(k)) {
          const o = next[k]?.order ?? 9999;
          next[k] = { ...next[k], order: Math.max(inboundOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('inbound-columns-config', JSON.stringify(next));
        localStorage.setItem('inbound-columns-version', ALL_COLUMNS_VERSION);
      } catch {}
      return next;
    });
  }, [inboundOrder]);
  
  // 調試信息：顯示可見列的數量
  console.log('Visible columns count:', columns.length);
  console.log('Column keys:', Object.entries(columnSettings).filter(([_, config]: [string, any]) => config.visible).map(([key]) => key));

  // 處理列可見性切換
  const handleColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const newSettings = {
      ...columnSettings,
      [columnKey]: { ...columnSettings[columnKey], visible }
    };
    setColumnSettings(newSettings);
    localStorage.setItem('inbound-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('inbound-columns-version', ALL_COLUMNS_VERSION);
  };

  // 顯示所有列 - 匹配新的14個預定入庫字段
  const showAllColumns = () => {
    const allVisibleSettings = {
      orderNumber: { visible: true, order: 0 },
      customerName: { visible: true, order: 1 },
      warehouseName: { visible: true, order: 2 },
      status: { visible: true, order: 3 },
      expectedQuantity: { visible: true, order: 4 },
      confirmedQuantity: { visible: true, order: 5 },
      logisticsMethod: { visible: true, order: 6 },
      trackingNumber: { visible: true, order: 7 },
      expectedArrivalDate: { visible: true, order: 8 },
      shipperContactName: { visible: true, order: 9 },
      shipperContactPhone: { visible: true, order: 10 },
      recipient: { visible: true, order: 11 },
      recipientAddress: { visible: true, order: 12 },
      recipientPhone: { visible: true, order: 13 },
      cargoRemark: { visible: true, order: 14 },
      actions: { visible: true, order: 15 }
    };
    setColumnSettings(allVisibleSettings);
    localStorage.setItem('inbound-columns-config', JSON.stringify(allVisibleSettings));
    localStorage.setItem('inbound-columns-version', ALL_COLUMNS_VERSION);
    message.success('所有列已顯示');
  };

  // 重置列配置 - 匹配新的14個預定入庫字段
  const resetColumnSettings = () => {
    const defaultSettings = {
      orderNumber: { visible: true, order: 0 },
      customerName: { visible: true, order: 1 },
      warehouseName: { visible: true, order: 2 },
      status: { visible: true, order: 3 },
      expectedQuantity: { visible: true, order: 4 },
      confirmedQuantity: { visible: true, order: 5 },
      logisticsMethod: { visible: true, order: 6 },
      trackingNumber: { visible: false, order: 7 },
      expectedArrivalDate: { visible: true, order: 8 },
      shipperContactName: { visible: false, order: 9 },
      shipperContactPhone: { visible: false, order: 10 },
      recipient: { visible: false, order: 11 },
      recipientAddress: { visible: false, order: 12 },
      recipientPhone: { visible: false, order: 13 },
      cargoRemark: { visible: false, order: 14 },
      actions: { visible: true, order: 15 }
    };
    setColumnSettings(defaultSettings);
    localStorage.setItem('inbound-columns-config', JSON.stringify(defaultSettings));
    localStorage.setItem('inbound-columns-version', ALL_COLUMNS_VERSION);
    message.success('列配置已重置');
  };

  // 列設置菜單
  const columnSettingsMenu = (
    <div style={{ 
      background: '#fff', 
      borderRadius: '2px', 
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>列設置</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
        {Object.entries(allColumns).map(([key, column]) => (
          <div 
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleColumnVisibilityChange(key, !(columnSettings[key]?.visible ?? true));
            }}
          >
            <Checkbox
              checked={columnSettings[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handleColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {typeof column.title === 'string' ? column.title : 
               key === 'orderNumber' ? '入庫單號' :
               key === 'customerName' ? '客戶' :
               key === 'warehouseName' ? '倉庫' :
               key === 'status' ? '狀態' :
               key === 'expectedQuantity' ? '預報數量' :
               key === 'confirmedQuantity' ? '收貨數量' :
               key === 'logisticsMethod' ? '物流方法' :
               key === 'trackingNumber' ? '物流追蹤號/參考資料' :
               key === 'expectedArrivalDate' ? '預計到達日期' :
               key === 'shipperContactName' ? '貨主聯絡人名' :
               key === 'shipperContactPhone' ? '貨主聯絡人電話' :
               key === 'recipient' ? '收件人' :
               key === 'recipientAddress' ? '收件地址' :
               key === 'recipientPhone' ? '收件電話' :
               key === 'cargoRemark' ? '貨物備註' :
               key === 'actions' ? '操作' : key}
            </Checkbox>
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid #f0f0f0', padding: '4px 0' }}>
        <div 
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            showAllColumns();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            顯示所有列
          </Button>
        </div>
        <div 
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            resetColumnSettings();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            重置列設置
          </Button>
        </div>
      </div>
    </div>
  );

  // 處理數據排序
  const sortedAndFilteredOrders = React.useMemo(() => {
    let result = [...filteredOrders];
    
    if (sortField && sortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[sortField as keyof InboundOrder];
        let bValue: any = b[sortField as keyof InboundOrder];
        
        // 處理日期排序
        if (sortField === 'expectedArrivalDate' || sortField === 'receiptDate' || sortField === 'createdAt') {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        }
        
        // 處理數字排序
    if (sortField === 'expectedQuantity' || sortField === 'confirmedQuantity') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理字符串排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }
    
    return result;
  }, [filteredOrders, sortField, sortOrder]);

  const statistics = {
    totalOrders: orders.length,
    upcoming: orders.filter(o => o.status === 'new').length,
    pending: 0,  // 目前不使用，保留字段
    received: orders.filter(o => o.status === 'received_quantity_correct' || o.status === 'received_quantity_incorrect').length,
    completed: orders.filter(o => o.status === 'received_quantity_correct' || o.status === 'received_quantity_incorrect').length,
    totalValue: 0,
    selectedCount: 0,
  };

  const renderOrderForm = () => (
    <Modal
      title={isReadOnlyMode ? '入庫訂單詳情' : (selectedOrder ? '編輯預定入庫' : '預定入庫')}
      open={orderModalVisible}
      onOk={() => { if (isReadOnlyMode) { setOrderModalVisible(false); setIsReadOnlyMode(false); } else { form.submit(); } }}
      onCancel={() => {
        setOrderModalVisible(false);
        setIsReadOnlyMode(false);
        setSelectedOrder(null);
        form.resetFields();
      }}
      footer={isReadOnlyMode ? [
        <Button key="close" onClick={() => { setOrderModalVisible(false); setIsReadOnlyMode(false); }}>
          關閉
        </Button>
      ] : undefined}
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        disabled={isReadOnlyMode}
        onFinish={async (values) => {
          try {
            if (selectedOrder) {
              // 計算所有產品明細的總預期數量
              const totalExpectedQuantity = orderItems.reduce((sum, item) => sum + (Number(item.expectedQuantity) || 0), 0);
              
              await apiService.updateInboundOrder(selectedOrder.id, {
                customerName: values.customer,
                warehouseName: values.warehouse,
                status: values.status,
                expectedQuantity: totalExpectedQuantity,
                baseUnit: 'PCS',
                logisticsMethod: values.logisticsMethod,
                trackingNumber: values.trackingNumber,
                shipperContactName: values.shipperContactName,
                shipperContactPhone: values.shipperContactPhone,
                cargoRemark: values.cargoRemark,
                expectedArrivalDate: values.expectedArrivalDate?.toDate(),
                receiptDate: values.receiptDate
              });
              // 本地同步
              setOrders(prev => prev.map(o => o.id === selectedOrder.id ? {
                ...o,
            customer: values.customer,
            customerName: values.customer,
            warehouse: values.warehouse,
            warehouseName: values.warehouse,
                status: values.status,
                expectedQuantity: totalExpectedQuantity,
            logisticsMethod: values.logisticsMethod,
            trackingNumber: values.trackingNumber,
            shipperContactName: values.shipperContactName,
            shipperContactPhone: values.shipperContactPhone,
                cargoRemark: values.cargoRemark,
                expectedArrivalDate: values.expectedArrivalDate?.toDate() || o.expectedArrivalDate,
                updatedAt: new Date()
              } : o));
              // 回顯詳情
              const fresh: any = await apiService.getInboundOrderById(selectedOrder.id);
              setSelectedOrder(prev => prev ? {
                ...prev,
                customer: fresh.customerName ?? values.customer,
                customerName: fresh.customerName ?? values.customer,
                warehouse: fresh.warehouseName ?? values.warehouse,
                warehouseName: fresh.warehouseName ?? values.warehouse,
                status: fresh.status ?? values.status,
                expectedQuantity: Number(fresh.expectedQuantity ?? values.expectedQuantity ?? 0),
                confirmedQuantity: Number(fresh.confirmedQuantity ?? prev.confirmedQuantity ?? 0),
                baseUnit: fresh.baseUnit ?? prev.baseUnit,
                logisticsMethod: fresh.logisticsMethod ?? values.logisticsMethod,
                trackingNumber: fresh.trackingNumber ?? values.trackingNumber,
                shipperContactName: fresh.shipperContactName ?? values.shipperContactName,
                shipperContactPhone: fresh.shipperContactPhone ?? values.shipperContactPhone,
                cargoRemark: fresh.cargoRemark ?? values.cargoRemark,
                expectedArrivalDate: fresh.expectedArrivalDate ? new Date(fresh.expectedArrivalDate) : (values.expectedArrivalDate?.toDate() || prev.expectedArrivalDate),
                receiptDate: fresh.receiptDate ? new Date(fresh.receiptDate) : (values.receiptDate || prev.receiptDate),
                updatedAt: fresh.updatedAt ? new Date(fresh.updatedAt) : new Date()
              } : prev);
            } else {
              const payload = {
                orderNumber: values.orderNumber,
                customerName: values.customer,
                warehouseName: values.warehouse,
                status: values.status || 'new',
                expectedQuantity: Number(values.expectedQuantity || 0),
                baseUnit: 'PCS',
                logisticsMethod: values.logisticsMethod,
                trackingNumber: values.trackingNumber,
                shipperContactName: values.shipperContactName,
                shipperContactPhone: values.shipperContactPhone,
                cargoRemark: values.cargoRemark,
                expectedArrivalDate: values.expectedArrivalDate?.toDate() || null,
                receiptDate: values.receiptDate || null
              };
              const resp: any = await apiService.createInboundOrder(payload);
              const r = resp?.data || resp;
              const newOrder = {
                id: String(r?.inboundId || r?.id || payload.orderNumber),
                orderNumber: r?.orderNumber || payload.orderNumber,
                customer: r?.customerName || payload.customerName,
                customerName: r?.customerName || payload.customerName,
                warehouse: r?.warehouseName || payload.warehouseName,
                warehouseName: r?.warehouseName || payload.warehouseName,
                status: r?.status || payload.status,
                expectedQuantity: Number(r?.expectedQuantity ?? payload.expectedQuantity ?? 0),
                confirmedQuantity: Number(r?.confirmedQuantity ?? 0),
                baseUnit: r?.baseUnit || 'piece',
                logisticsMethod: r?.logisticsMethod || payload.logisticsMethod,
                trackingNumber: r?.trackingNumber || payload.trackingNumber,
                shipperContactName: r?.shipperContactName || payload.shipperContactName,
                shipperContactPhone: r?.shipperContactPhone || payload.shipperContactPhone,
                cargoRemark: r?.cargoRemark || payload.cargoRemark,
                expectedArrivalDate: r?.expectedArrivalDate ? new Date(r.expectedArrivalDate) : (payload.expectedArrivalDate || new Date()),
                receiptDate: r?.receiptDate ? new Date(r.receiptDate) : undefined,
                createdAt: r?.createdAt ? new Date(r.createdAt) : new Date(),
                updatedAt: r?.updatedAt ? new Date(r.updatedAt) : new Date(),
                createdBy: 'system',
                items: []
              } as any;
              setOrders(prev => [newOrder, ...prev]);

              // 若在新建單據前已添加了產品（暫存在前端），此處一併落庫
              if (Array.isArray(orderItems) && orderItems.length > 0) {
                try {
                  await Promise.all(orderItems.map((it) => {
                    const itemPayload: any = {
                      id: it.id || undefined,
                      productSku: it.productSku || null,
                      productName: it.productName || null,
                      lotNumber: it.lotNumber || null,
                      serialNumber: it.serialNumber || null,
                      expiryDate: it.expiryDate || null,
                      baseUnit: it.baseUnit || null,
                      packagingUnit: it.packagingUnit || null,
                      temperatureId: it.temperatureId ? Number(it.temperatureId) : null,
                      expectedQuantity: Number(it.expectedQuantity ?? 0),
                      confirmedQuantity: Number(it.confirmedQuantity ?? 0),
                      currentLocation: it.currentLocation || null,
                      currentStock: Number(it.currentStock ?? 0),
                      remark: it.remark || null,
                      length: it.length ?? null,
                      width: it.width ?? null,
                      height: it.height ?? null,
                      weight: it.weight ?? null,
                      userName: it.userName || 'system'
                    };
                    return apiService.upsertInboundOrderItem(newOrder.id, itemPayload);
                  }));
                  // 以後端為準重新拉取一次明細
                  try {
                    const latest: any = await apiService.getInboundOrderItems(newOrder.id);
                    const items = latest?.data || [];
                    if (Array.isArray(items)) setOrderItems(items as any);
                  } catch {}
                } catch {}
              }
            }
          message.success('訂單保存成功');
          } catch (e) {
            message.error('保存失敗');
          } finally {
          setOrderModalVisible(false);
          setSelectedOrder(null);
          form.resetFields();
          }
        }}
      >
        {/* 第一行：入庫單號、客戶 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="orderNumber"
              label="入庫單號"
            >
              <Input placeholder="系統自動生成" disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="customer"
              label="客戶"
              rules={[{ required: true, message: '請選擇客戶' }]}
            >
              <Select placeholder="選擇客戶" showSearch>
                <Option value="ABC Company Ltd.">ABC Company Ltd.</Option>
                <Option value="XYZ Trading Co.">XYZ Trading Co.</Option>
                <Option value="Global Trading Inc.">Global Trading Inc.</Option>
                <Option value="Asia Import Export">Asia Import Export</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* 第二行：倉庫、狀態 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="warehouse"
              label="倉庫"
              rules={[{ required: true, message: '請選擇倉庫' }]}
            >
              <Select placeholder="選擇倉庫">
                {(warehouses.length ? warehouses : mockWarehouses).map(warehouse => (
                  <Option key={warehouse.id} value={warehouse.name}>
                    {warehouse.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            {selectedOrder ? (
              // 編輯模式：顯示選擇框（不包含New選項）
              <Form.Item
                name="status"
                label="狀態"
                rules={[{ required: true, message: '請選擇狀態' }]}
              >
                <Select placeholder="選擇狀態">
                  <Option value="received_quantity_correct">已收貨 數量正確</Option>
                  <Option value="received_quantity_incorrect">已收貨 數量不正確</Option>
                </Select>
              </Form.Item>
            ) : (
              // 新建模式：顯示純文字 "狀態: New"
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.85)', marginBottom: 8 }}>
                  狀態
                </div>
                <Tag 
                  color="blue" 
                  style={{ 
                    fontSize: '14px', 
                    fontWeight: 'bold',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    border: '2px solid #1890ff',
                    backgroundColor: '#f0f8ff'
                  }}
                >
                  <ClockCircleOutlined style={{ marginRight: '4px' }} />
                  New
                </Tag>
                {/* 隱藏的輸入框確保表單提交時包含 status 值 */}
                <Form.Item name="status" noStyle>
                  <Input type="hidden" />
                </Form.Item>
              </div>
            )}
          </Col>
        </Row>

        {/* 第三行：物流方法 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="logisticsMethod"
              label="物流方法"
            >
              <Select placeholder="選擇物流方法">
                <Option value="速遞公司">速遞公司</Option>
                <Option value="SF Express">SF Express - 順豐速運</Option>
                <Option value="DHL Express">DHL Express - DHL速遞</Option>
                <Option value="FedEx">FedEx - 聯邦快遞</Option>
                <Option value="郵政服務">郵政服務</Option>
                <Option value="自送">自送</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* 第四行：物流追蹤號/參考資料、預計到達日期 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="trackingNumber"
              label="物流追蹤號/參考資料"
            >
              <Input placeholder="輸入追蹤號或參考資料" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="expectedArrivalDate"
              label="預計到達日期"
              rules={[{ required: true, message: '請選擇預計到達日期' }]}
            >
              <DatePicker style={{ width: '100%' }} placeholder="選擇預計到達日期" />
            </Form.Item>
          </Col>
        </Row>

        {/* 第五行：貨主聯絡人名、貨主聯絡人電話 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="shipperContactName"
              label="貨主聯絡人名"
              rules={[{ required: true, message: '請輸入貨主聯絡人名' }]}
            >
              <Input placeholder="輸入貨主聯絡人名" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="shipperContactPhone"
              label="貨主聯絡人電話"
              rules={[{ required: true, message: '請輸入貨主聯絡人電話' }]}
            >
              <Input placeholder="輸入貨主聯絡人電話" />
            </Form.Item>
          </Col>
        </Row>

        {/* 第六行：貨物備註 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
                name="cargoRemark"
                label="貨物備註"
            >
              <Input.TextArea rows={2} placeholder="輸入貨物相關備註" />
            </Form.Item>
          </Col>
        </Row>

        {/* 第七行：收件人、收件電話 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="recipient"
              label="收件人"
              tooltip="默認為物流公司名稱，來自公司設置"
            >
              <Input placeholder="Shoptoplus (來自公司設置)" disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="recipientPhone"
              label="收件電話"
              tooltip="自動填充，來自公司設置中的聯絡電話"
            >
              <Input placeholder="自動填充聯絡電話 (來自公司設置)" disabled />
            </Form.Item>
          </Col>
        </Row>

        {/* 第八行：收件地址 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="recipientAddress"
              label="收件地址"
              tooltip="自動填充，來自公司設置中的送貨地址"
            >
              <Input.TextArea rows={2} placeholder="自動填充送貨地址 (來自公司設置)" disabled />
            </Form.Item>
          </Col>
        </Row>



        {/* Product Management Section */}
        <Divider orientation="left">產品管理</Divider>
        
        {!isReadOnlyMode && (
          <div style={{ marginBottom: 16 }}>
            <Button 
              type="dashed" 
              icon={<PlusOutlined />} 
              onClick={handleAddItem}
              style={{ width: '100%' }}
            >
              添加產品
            </Button>
          </div>
        )}

        {orderItems.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Alert
              message={`已添加 ${orderItems.length} 個產品`}
              description={`總數量: ${orderItems.reduce((sum, item) => sum + item.expectedQuantity, 0)} 件`}
              type="info"
              showIcon
            />
          </div>
        )}

        {/* 產品列表表格 */}
        <div style={{ overflowX: 'auto', border: '1px solid #f0f0f0', borderRadius: '6px', position: 'relative' }}>
          <div style={{ minWidth: '1400px' }}>
            {/* 表頭 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(13, 1fr)',
              gap: '1px',
              backgroundColor: '#fafafa',
              borderBottom: '1px solid #f0f0f0',
              position: 'relative'
            }}>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                產品名稱
              </div>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                SKU
              </div>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                預報數量
              </div>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                單位
              </div>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                重量
              </div>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                長度 (cm)
              </div>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                寬度 (cm)
              </div>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                高度 (cm)
              </div>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                CBM
              </div>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                包裝
              </div>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                批次號
              </div>
              <div style={{ padding: '12px 8px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                序號
              </div>
              <div style={{
                padding: '12px 8px',
                fontWeight: 'bold',
                fontSize: '14px',
                textAlign: 'center',
                position: 'sticky',
                right: 0,
                backgroundColor: '#ebebeb',
                zIndex: 1
              }}>
                操作
              </div>
            </div>

            {/* 表格內容 */}
            {orderItems.map((item, index) => (
              <div key={item.id} style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(13, 1fr)',
                gap: '1px',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                minHeight: '60px',
                alignItems: 'center'
              }}>
                {/* 產品名稱 */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text strong style={{ fontSize: '14px' }}>{item.productName}</Text>
                </div>

                {/* SKU */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '13px' }}>{item.productSku}</Text>
                </div>

                {/* 預報數量 */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  {isReadOnlyMode ? (
                    <Text style={{ fontSize: '13px' }}>{item.expectedQuantity}</Text>
                  ) : (
                    <InputNumber
                      size="small"
                      min={1}
                      value={item.expectedQuantity}
                      onChange={(value) => handleUpdateItemQuantity(index, value || 1)}
                      style={{ width: '80px' }}
                    />
                  )}
                </div>

                {/* 單位 */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text style={{ fontSize: '13px' }}>{item.baseUnit || 'N/A'}</Text>
                </div>

                {/* 重量 */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text style={{ fontSize: '13px' }}>{item.weight || 'N/A'} kg</Text>
                </div>

                {/* 長度 (cm) */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text style={{ fontSize: '13px' }}>{item.length ?? 'N/A'}</Text>
                </div>
                {/* 寬度 (cm) */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text style={{ fontSize: '13px' }}>{item.width ?? 'N/A'}</Text>
                </div>
                {/* 高度 (cm) */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text style={{ fontSize: '13px' }}>{item.height ?? 'N/A'}</Text>
                </div>

                {/* CBM */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text style={{ fontSize: '13px' }}>
                    {typeof item.packageCBM === 'number' && Number.isFinite(item.packageCBM)
                      ? `${item.packageCBM.toFixed(6)} m³`
                      : 'N/A'}
                  </Text>
                </div>

                {/* 包裝 */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text style={{ fontSize: '13px' }}>{item.packagingUnit || 'BOX'}</Text>
                </div>

                {/* 批次號 */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {item.lotNumber || 'N/A'}
                  </Text>
                </div>
                {/* 序號 */}
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {item.serialNumber || 'N/A'}
                  </Text>
                </div>

                {/* 操作 */}
                <div style={{
                  padding: '8px',
                  textAlign: 'center',
                  position: 'sticky',
                  right: 0,
                  backgroundColor: '#ebebeb',
                  zIndex: 1
                }}>
                  {isReadOnlyMode ? (
                    <Text type="secondary" style={{ fontSize: '12px' }}>-</Text>
                  ) : (
                    <Space direction="vertical" size="small">
                      <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditItem(index)}
                        style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                      >
                        編輯
                      </Button>
                      <Popconfirm
                        title="確定要移除這個產品嗎？"
                        onConfirm={() => handleRemoveItem(index)}
                        okText="確定"
                        cancelText="取消"
                      >
                        <Button
                          type="link"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                        >
                          移除
                        </Button>
                      </Popconfirm>
                    </Space>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

      </Form>
    </Modal>
  );

  const renderItemForm = () => (
    <Modal
      title={editingItemIndex >= 0 ? '編輯產品' : '添加產品'}
      open={itemModalVisible}
      onOk={() => itemForm.submit()}
      onCancel={() => {
        setItemModalVisible(false);
        setEditingItemIndex(-1);
        setSelectedItem(null);
        itemForm.resetFields();
      }}
      width={800}
    >
      <Form
        form={itemForm}
        layout="vertical"
        onFinish={handleSubmitItem}
      >
        {/* 第一行：產品SKU、產品名稱 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="productSku"
              label="產品SKU"
              rules={[{ required: true, message: '請選擇產品SKU' }]}
            >
              <Select
                showSearch
                placeholder="請輸入產品SKU或名稱搜尋 (最少2個字)"
                loading={productsLoading}
                filterOption={false}
                onSearch={handleProductSearch}
                notFoundContent={productsLoading ? '搜尋中...' : '找不到產品，請輸入關鍵字搜尋'}
                options={products.map(product => ({
                  value: product.sku,
                  label: `${product.sku} - ${product.name}`,
                  product
                }))}
                onSelect={(value, option: any) => {
                  const product = option.product;
                  itemForm.setFieldsValue({
                    productId: product.id,
                    productName: product.name,
                    length: product.length || undefined,
                    width: product.width || undefined,
                    height: product.height || undefined,
                    weight: product.weight || undefined,
                    packageCBM: typeof product.volume === 'number' && Number.isFinite(product.volume) ? product.volume : undefined,
                    packagingUnit: product.packagingUnit || 'BOX',
                    baseUnit: product.baseUnit || 'PCS'
                  });
                  // Reset products to initial state after selection
                  loadInitialProducts();
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="productName"
              label="產品名稱"
              rules={[{ required: true, message: '產品名稱將自動帶出' }]}
            >
              <Input readOnly placeholder="將自動帶出" />
            </Form.Item>
          </Col>
        </Row>

        {/* 第二行：預報數量、批次號 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="expectedQuantity"
              label="預報數量"
              rules={[{ required: true, message: '請輸入預報數量' }]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                placeholder="預報數量"
                value={editingItemExpectedQty}
                onChange={(val) => {
                  const qty = val && val > 0 ? val : 1;
                  setEditingItemExpectedQty(qty);
                  itemForm.setFieldsValue({ expectedQuantity: qty });
                  if (editingItemIndex >= 0) {
                    handleUpdateItemQuantity(editingItemIndex, qty);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lotNumber"
              label="批次號"
            >
              <Input placeholder="批次號" />
            </Form.Item>
          </Col>
        </Row>

        {/* 第三行：序號、基本單位 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="serialNumber"
              label="序號"
            >
              <Input placeholder="序號" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="baseUnit"
              label="基本單位"
              rules={[{ required: true, message: '請選擇基本單位' }]}
            >
              <Select placeholder="選擇基本單位" showSearch>
                {mockBaseUnits.map(unit => (
                  <Option key={unit.value} value={unit.value}>
                    {unit.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* 現時貨位 */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="currentLocation"
              label="現時貨位"
            >
              <TreeSelect
                style={{ width: '100%' }}
                placeholder={locationsLoading ? '載入中...' : locationTreeData.length === 0 ? '請先選擇入庫單倉庫' : '選擇貨位'}
                treeData={locationTreeData}
                // treeDefaultExpandAll  // 屏蔽 這行代碼 已將入庫頁的 TreeSelect 改為預設不展開，只保留層級線條
                treeLine
                showSearch
                allowClear
                disabled={locationsLoading || locationTreeData.length === 0}
                treeNodeLabelProp="displayLabel"
                treeNodeFilterProp="searchText"
                filterTreeNode={(input, treeNode) => {
                  const searchText = ((treeNode as any)?.searchText || '').toLowerCase();
                  return searchText.includes(input.toLowerCase());
                }}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                notFoundContent={locationsLoading ? '載入中...' : '無可用貨位'}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 第四行：長度、寬度 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="length"
              label="長度 (cm)"
            >
              <InputNumber 
                min={0} 
                style={{ width: '100%' }} 
                placeholder="長度"
                onChange={() => {
                  // Auto-calculate CBM when dimensions change
                  setTimeout(() => {
                    const length = itemForm.getFieldValue('length');
                    const width = itemForm.getFieldValue('width');
                    const height = itemForm.getFieldValue('height');
                    if (length && width && height) {
                      // 正確的CBM計算：cm³ → m³ (除以1,000,000)
                    const cbm = (length * width * height) / 1000000;
                    itemForm.setFieldsValue({ packageCBM: Number(cbm.toFixed(6)) });
                    }
                  }, 100);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="width"
              label="寬度 (cm)"
            >
              <InputNumber 
                min={0} 
                style={{ width: '100%' }} 
                placeholder="寬度"
                onChange={() => {
                  // Auto-calculate CBM when dimensions change
                  setTimeout(() => {
                    const length = itemForm.getFieldValue('length');
                    const width = itemForm.getFieldValue('width');
                    const height = itemForm.getFieldValue('height');
                    if (length && width && height) {
                      // 正確的CBM計算：cm³ → m³ (除以1,000,000)
                    const cbm = (length * width * height) / 1000000;
                    itemForm.setFieldsValue({ packageCBM: Number(cbm.toFixed(6)) });
                    }
                  }, 100);
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 第五行：高度、重量 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="height"
              label="高度 (cm)"
            >
              <InputNumber 
                min={0} 
                style={{ width: '100%' }} 
                placeholder="高度" 
                onChange={() => {
                  // Auto-calculate CBM when dimensions change
                  setTimeout(() => {
                    const length = itemForm.getFieldValue('length');
                    const width = itemForm.getFieldValue('width');
                    const height = itemForm.getFieldValue('height');
                    if (length && width && height) {
                      // 正確的CBM計算：cm³ → m³ (除以1,000,000)
                    const cbm = (length * width * height) / 1000000;
                    itemForm.setFieldsValue({ packageCBM: Number(cbm.toFixed(6)) });
                    }
                  }, 100);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="weight"
              label="重量 (kg)"
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="重量" />
            </Form.Item>
          </Col>
        </Row>

        {/* 第六行：包裝CBM、包裝單位 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="packageCBM"
              label="包裝CBM (立方米)"
              tooltip="包裝體积（立方米）。如与SKU設置不同，将使用此值。可手動覆盖自動計算值。"
            >
              <InputNumber 
                min={0} 
                step={0.000001}
                style={{ width: '100%' }} 
                placeholder="自動計算或手動輸入" 
                precision={6}
                addonAfter="m³"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="packagingUnit"
              label="包裝單位"
              rules={[{ required: true, message: '請選擇包裝單位' }]}
            >
              <Select placeholder="選擇包裝單位" showSearch>
                <Option value="BOX">箱 (BOX)</Option>
                <Option value="CARTON">纸箱 (CARTON)</Option>
                <Option value="PALLET">托盤 (PALLET)</Option>
                <Option value="BAG">袋 (BAG)</Option>
                <Option value="ROLL">卷 (ROLL)</Option>
                <Option value="BOTTLE">瓶 (BOTTLE)</Option>
                <Option value="PIECE">件 (PIECE)</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* 第六行：到期日期 + 溫度要求 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="expiryDate"
              label="到期日期"
            >
              <DatePicker style={{ width: '100%' }} placeholder="選擇到期日期" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="temperatureId"
              label="溫度要求"
            >
              <Select placeholder="選擇溫度要求" allowClear options={(temperatureOptions || []).map((t: any) => ({ value: t.id, label: t.name }))} />
            </Form.Item>
          </Col>
        </Row>

        {/* 備註單獨一行 */}
        <Form.Item
          name="remark"
          label="備註"
        >
          <Input.TextArea rows={2} placeholder="產品備註" />
        </Form.Item>
      </Form>
    </Modal>
  );



  const renderViewOrderModal = () => (
    <Modal
      title="入庫訂單詳情"
      open={viewModalVisible}
      onCancel={() => setViewModalVisible(false)}
      footer={[
        <Button key="close" onClick={() => setViewModalVisible(false)}>
          關閉
        </Button>
      ]}
      width={1000}
    >
      {selectedOrder && (
        <div>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="入庫單號" span={2}>
              <Text code>{selectedOrder.orderNumber}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="客戶">
              {selectedOrder.customerName}
            </Descriptions.Item>
            <Descriptions.Item label="倉庫">
              {selectedOrder.warehouseName}
            </Descriptions.Item>
            <Descriptions.Item label="狀態">
              <Tag color={getStatusColor(selectedOrder.status)}>
                {getStatusText(selectedOrder.status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="預期數量">
              {selectedOrder.expectedQuantity} {selectedOrder.baseUnit}
            </Descriptions.Item>
            <Descriptions.Item label="批次號">
              {selectedOrder.lotNumber || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="序號">
              {selectedOrder.serialNumber || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="尺寸">
              {selectedOrder.dimensions ? 
                `${selectedOrder.dimensions.length}×${selectedOrder.dimensions.width}×${selectedOrder.dimensions.height} ${selectedOrder.dimensions.unit}` : 
                'N/A'
              }
            </Descriptions.Item>
            <Descriptions.Item label="基本單位">
              {selectedOrder.baseUnit}
            </Descriptions.Item>
            <Descriptions.Item label="包裝單位">
              {selectedOrder.packagingUnit}
            </Descriptions.Item>
            <Descriptions.Item label="物流方式">
              {selectedOrder.logisticsMethod}
            </Descriptions.Item>
            <Descriptions.Item label="追蹤號碼">
              {(selectedOrder as any)?.trackingNumber || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="參考號碼">
              {(selectedOrder as any)?.referenceNumber || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="預期到達日期">
              {selectedOrder.expectedArrivalDate instanceof Date 
                ? selectedOrder.expectedArrivalDate.toLocaleDateString()
                : new Date(selectedOrder.expectedArrivalDate).toLocaleDateString()}
            </Descriptions.Item>
            <Descriptions.Item label="收貨日期">
              {selectedOrder.receiptDate ? 
                (selectedOrder.receiptDate instanceof Date 
                  ? selectedOrder.receiptDate.toLocaleDateString()
                  : new Date(selectedOrder.receiptDate).toLocaleDateString()) : 
                'N/A'
              }
            </Descriptions.Item>
            <Descriptions.Item label="發貨人聯繫人">
              {selectedOrder.shipperContactName || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="發貨人電話">
              {selectedOrder.shipperContactPhone || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="收件人">
              {selectedOrder.recipient || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="收件人電話">
              {selectedOrder.recipientPhone || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="收件地址" span={2}>
              {selectedOrder.recipientAddress ? 
                `${selectedOrder.recipientAddress.street}, ${selectedOrder.recipientAddress.city}, ${selectedOrder.recipientAddress.state} ${selectedOrder.recipientAddress.postalCode}, ${selectedOrder.recipientAddress.country}` : 
                'N/A'
              }
            </Descriptions.Item>
            <Descriptions.Item label="貨物備註" span={2}>
              {selectedOrder.cargoRemark || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="創建時間">
              {selectedOrder.createdAt instanceof Date 
                ? selectedOrder.createdAt.toLocaleString()
                : new Date(selectedOrder.createdAt).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="更新時間">
              {selectedOrder.updatedAt instanceof Date 
                ? selectedOrder.updatedAt.toLocaleString()
                : new Date(selectedOrder.updatedAt).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="創建人">
              {selectedOrder.createdBy}
            </Descriptions.Item>
            <Descriptions.Item label="條形碼">
              {selectedOrder.barcode || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="QR碼" span={2}>
              {selectedOrder.qrCode || 'N/A'}
            </Descriptions.Item>
          </Descriptions>
          
          {/* 產品列表 */}
          {selectedOrder.items && selectedOrder.items.length > 0 && (
            <>
              <Divider orientation="left">產品詳情</Divider>
              <Table
                dataSource={selectedOrder.items}
                columns={[
                  {
                    title: '產品名稱',
                    dataIndex: 'productName',
                    key: 'productName',
                    render: (text: string) => <Text strong>{text}</Text>,
                  },
                  {
                    title: 'SKU',
                    dataIndex: 'productSku',
                    key: 'productSku',
                    render: (sku: string) => <Text type="secondary" style={{ fontSize: '12px' }}>{sku}</Text>,
                  },
                  {
                    title: '預報數量',
                    dataIndex: 'expectedQuantity',
                    key: 'expectedQuantity',
                  },
                  {
                    title: '長度 (cm)',
                    dataIndex: 'length',
                    key: 'length',
                  },
                  {
                    title: '寬度 (cm)',
                    dataIndex: 'width',
                    key: 'width',
                  },
                  {
                    title: '高度 (cm)',
                    dataIndex: 'height',
                    key: 'height',
                  },
                  {
                    title: '重量 (kg)',
                    dataIndex: 'weight',
                    key: 'weight',
                  },
                  {
                    title: '批次號',
                    dataIndex: 'lotNumber',
                    key: 'lotNumber',
                    render: (lot: string) => lot || 'N/A',
                  },
                  {
                    title: '序號',
                    dataIndex: 'serialNumber',
                    key: 'serialNumber',
                    render: (sn: string) => sn || 'N/A',
                  },
                  {
                    title: '到期日期',
                    dataIndex: 'expiryDate',
                    key: 'expiryDate',
                    render: (date) => date || 'N/A',
                  },
                  {
                    title: '備註',
                    dataIndex: 'remark',
                    key: 'remark',
                    render: (remark) => remark || 'N/A',
                  },
                ]}
                pagination={false}
                size="small"
              />
            </>
          )}
        </div>
      )}
    </Modal>
  );

  const renderFieldConfigModal = () => (
    <Modal
      title="配置批量上傳字段"
      open={fieldConfigModalVisible}
      onOk={() => fieldConfigForm.submit()}
      onCancel={() => setFieldConfigModalVisible(false)}
      width={800}
    >
      <Form
        form={fieldConfigForm}
        layout="vertical"
        onFinish={handleSaveFieldConfig}
      >
        <Alert
          message="字段配置說明"
          description="勾選的字段將在批量上傳時作為必填字段進行驗證。未勾選的字段為可選字段。"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
        
        <Row gutter={16}>
          {Object.entries(fieldConfig).map(([key, config]) => (
            <Col span={12} key={key}>
              <Form.Item
                name={key}
                valuePropName="checked"
              >
                <Checkbox>
                  <div>
                    <Text strong>{config.label}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {config.description}
                    </Text>
                  </div>
                </Checkbox>
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form>
    </Modal>
  );

  // 基於打印入庫單生成PDF下載
  const generatePDFFromPrintModal = useCallback(async () => {
    try {
      console.log('=== 開始PDF生成調試 ===');
      console.log('selectedOrder:', selectedOrder);
      
      // 檢查必要的庫是否已加載
      const html2canvas = (window as any).html2canvas;
      const jsPDF = (window as any).jsPDF;
      
      console.log('html2canvas type:', typeof html2canvas);
      console.log('jsPDF type:', typeof jsPDF);
      console.log('html2canvas:', html2canvas);
      console.log('jsPDF:', jsPDF);
      
      if (!html2canvas) {
        console.error('html2canvas未加載');
        message.error('html2canvas庫未加載，請刷新頁面重試');
        return;
      }
      
      if (!jsPDF) {
        console.error('jsPDF未加載');
        message.error('jsPDF庫未加載，請刷新頁面重試');
        return;
      }

      if (!selectedOrder) {
        console.error('selectedOrder為空');
        message.error('請先選擇要下載的入庫單');
        return;
      }

      // 嘗試多種選擇器來查找打印內容
      console.log('=== 查找打印內容 ===');
      
      // 方法1: 通用模態框內容
      let printModalContent = document.querySelector('.ant-modal-body') as HTMLElement;
      console.log('方法1 - .ant-modal-body:', printModalContent);
      
      // 方法2: 特定樣式的div
      if (!printModalContent) {
        printModalContent = document.querySelector('.ant-modal-body > div[style*="width: 100%"]') as HTMLElement;
        console.log('方法2 - div with width 100%:', printModalContent);
      }
      
      // 方法3: 查找任何可見的模態框內容
      if (!printModalContent) {
        const allModalBodies = document.querySelectorAll('.ant-modal-body');
        console.log('所有模態框:', allModalBodies);
        if (allModalBodies.length > 0) {
          printModalContent = allModalBodies[allModalBodies.length - 1] as HTMLElement; // 取最後一個
          console.log('方法3 - 最後一個模態框:', printModalContent);
        }
      }
      
      if (!printModalContent) {
        console.error('找不到打印內容');
        message.error('找不到打印內容，請確保打印預覽已打開');
        return;
      }

      console.log('找到打印內容元素:', printModalContent);
      console.log('元素尺寸:', {
        width: printModalContent.offsetWidth,
        height: printModalContent.offsetHeight,
        scrollWidth: printModalContent.scrollWidth,
        scrollHeight: printModalContent.scrollHeight
      });

      // 生成QR碼並更新到打印內容中
      try {
        console.log('=== 生成QR碼 ===');
        const qrCodeUrl = selectedOrder.qrCode || `${window.location.origin}/inbound/${selectedOrder.orderNumber}`;
        console.log('QR碼URL:', qrCodeUrl);
        
        const qrCodeDataUrl = await QRCode.toDataURL(qrCodeUrl, {
          width: 84,
          margin: 1
        });
        console.log('QR碼生成成功');
        
        // 查找並更新QR碼圖片
        const qrCodeImg = printModalContent.querySelector('img[alt="QR Code"]') as HTMLImageElement;
        console.log('找到QR碼圖片元素:', qrCodeImg);
        
        if (qrCodeImg && qrCodeDataUrl) {
          qrCodeImg.src = qrCodeDataUrl;
          console.log('QR碼已更新到DOM');
        }
      } catch (error) {
        console.error('QR碼生成失敗:', error);
      }

      // 等待一下確保QR碼加載完成
      console.log('等待QR碼加載...');
      await new Promise(resolve => setTimeout(resolve, 500));

      // 修復條形碼顯示問題 - 將CSS gradient轉換為可見的條形碼
      console.log('=== 修復條形碼顯示 ===');
      const barcodeElements = printModalContent.querySelectorAll('[style*="repeating-linear-gradient"]');
      console.log('找到條形碼元素:', barcodeElements.length);
      
      let barcodeFixElements: { element: HTMLElement, originalStyle: string }[] = [];
      
      barcodeElements.forEach((element, index) => {
        const barcodeEl = element as HTMLElement;
        console.log(`處理條形碼元素 ${index + 1}:`, barcodeEl);
        
        // 保存原始樣式
        barcodeFixElements.push({
          element: barcodeEl,
          originalStyle: barcodeEl.style.cssText
        });
        
        // 創建一個canvas來繪製條形碼
        const barcodeCanvas = document.createElement('canvas');
        barcodeCanvas.width = 120;
        barcodeCanvas.height = 25;
        const ctx = barcodeCanvas.getContext('2d');
        
        if (ctx) {
          // 繪製條形碼圖案
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, 120, 25);
          
          // 繪製條形碼條紋
          ctx.fillStyle = '#000000';
          for (let i = 0; i < 120; i += 4) {
            if (Math.floor(i / 2) % 2 === 0) {
              ctx.fillRect(i, 0, 2, 25);
            }
          }
          
          // 將canvas轉換為圖片並替換元素背景
          const barcodeDataUrl = barcodeCanvas.toDataURL('image/png');
          barcodeEl.style.background = `url(${barcodeDataUrl})`;
          barcodeEl.style.backgroundSize = 'cover';
          barcodeEl.style.backgroundRepeat = 'no-repeat';
          barcodeEl.style.width = '120px';
          barcodeEl.style.height = '25px';
          barcodeEl.style.display = 'inline-block';
          
          console.log(`條形碼 ${index + 1} 已轉換為圖片背景`);
        }
      });

      // 使用html2canvas渲染打印內容
      console.log('=== 開始html2canvas渲染 ===');
      
      const canvas = await html2canvas(printModalContent, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: printModalContent.scrollWidth,
        height: printModalContent.scrollHeight,
        logging: true  // 開啟html2canvas日誌
      });

      console.log('html2canvas渲染完成！');
      console.log('畫佈尺寸:', canvas.width, 'x', canvas.height);

      // 創建PDF
      console.log('=== 創建PDF ===');
      const pdf = new jsPDF('p', 'mm', 'a4');
      console.log('jsPDF實例創建成功');
      
      const imgData = canvas.toDataURL('image/png', 1.0);
      console.log('畫佈轉換為圖片數據完成，數據長度:', imgData.length);
      
      // 計算圖片在PDF中的尺寸
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      console.log('PDF尺寸計算:', {
        pdfWidth,
        pdfHeight,
        imgWidth,
        imgHeight
      });
      
      let position = 0;
      let heightLeft = imgHeight;

      // 添加第一頁
      console.log('添加第一頁到PDF...');
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      // 如果內容超過一頁，添加額外頁面
      let pageCount = 1;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
        pageCount++;
        console.log(`添加第${pageCount}頁...`);
      }

      // 下載PDF文件
      const fileName = `入庫單-${selectedOrder.orderNumber}-${dayjs().format('YYYYMMDD')}.pdf`;
      console.log('=== 準備下載PDF ===');
      console.log('文件名:', fileName);
      
      pdf.save(fileName);
      console.log('PDF下載指令已執行');
      
      // 恢復條形碼元素的原始樣式
      console.log('=== 恢復條形碼原始樣式 ===');
      barcodeFixElements.forEach((item, index) => {
        item.element.style.cssText = item.originalStyle;
        console.log(`條形碼 ${index + 1} 樣式已恢復`);
      });
      
      message.success('PDF文件已成功下載！');
      console.log('=== PDF生成完成 ===');
      
    } catch (error) {
      console.error('=== PDF生成錯誤 ===');
      console.error('錯誤詳情:', error);
      console.error('錯誤堆棧:', error instanceof Error ? error.stack : 'No stack trace');
      
      // 在錯誤情況下也要恢復條形碼樣式
      try {
        // Note: barcodeFixElements would need to be declared in the wider scope
        // For now, just log the error
        console.log('錯誤處理：需要恢復條形碼樣式');
      } catch (restoreError) {
        console.error('恢復條形碼樣式時出錯:', restoreError);
      }
      
      message.error(`PDF生成失敗: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [selectedOrder]);

  const renderPrintModal = () => {
    // 生成條形碼的CSS樣式
    const generateBarcodeCSS = (text: string) => {
      return {
        background: `repeating-linear-gradient(
          90deg,
          #000 0px, #000 1px, transparent 1px, transparent 2px,
          #000 2px, #000 3px, transparent 3px, transparent 4px,
          #000 4px, #000 5px, transparent 5px, transparent 6px,
          #000 6px, #000 7px, transparent 7px, transparent 8px,
          #000 8px, #000 9px, transparent 9px, transparent 10px,
          #000 10px, #000 11px, transparent 11px, transparent 12px,
          #000 12px, #000 13px, transparent 13px, transparent 14px,
          #000 14px, #000 15px, transparent 15px, transparent 16px,
          #000 16px, #000 17px, transparent 17px, transparent 18px,
          #000 18px, #000 19px, transparent 19px, transparent 20px,
          #000 20px, #000 21px, transparent 21px, transparent 22px,
          #000 22px, #000 23px, transparent 23px, transparent 24px,
          #000 24px, #000 25px, transparent 25px, transparent 26px,
          #000 26px, #000 27px, transparent 27px, transparent 28px,
          #000 28px, #000 29px, transparent 29px, transparent 30px,
          #000 30px, #000 31px, transparent 31px, transparent 32px,
          #000 32px, #000 33px, transparent 33px, transparent 34px,
          #000 34px, #000 35px, transparent 35px, transparent 36px,
          #000 36px, #000 37px, transparent 37px, transparent 38px,
          #000 38px, #000 39px, transparent 39px, transparent 40px,
          #000 40px, #000 41px, transparent 41px, transparent 42px,
          #000 42px, #000 43px, transparent 43px, transparent 44px,
          #000 44px, #000 45px, transparent 45px, transparent 46px,
          #000 46px, #000 47px, transparent 47px, transparent 48px,
          #000 48px, #000 49px, transparent 49px, transparent 50px,
          #000 50px, #000 51px, transparent 51px, transparent 52px,
          #000 52px, #000 53px, transparent 53px, transparent 54px,
          #000 54px, #000 55px, transparent 55px, transparent 56px,
          #000 56px, #000 57px, transparent 57px, transparent 58px,
          #000 58px, #000 59px, transparent 59px, transparent 60px,
          #000 60px, #000 61px, transparent 61px, transparent 62px,
          #000 62px, #000 63px, transparent 63px, transparent 64px,
          #000 64px, #000 65px, transparent 65px, transparent 66px,
          #000 66px, #000 67px, transparent 67px, transparent 68px,
          #000 68px, #000 69px, transparent 69px, transparent 70px,
          #000 70px, #000 71px, transparent 71px, transparent 72px,
          #000 72px, #000 73px, transparent 73px, transparent 74px,
          #000 74px, #000 75px, transparent 75px, transparent 76px,
          #000 76px, #000 77px, transparent 77px, transparent 78px,
          #000 78px, #000 79px, transparent 79px, transparent 80px,
          #000 80px, #000 81px, transparent 81px, transparent 82px,
          #000 82px, #000 83px, transparent 83px, transparent 84px,
          #000 84px, #000 85px, transparent 85px, transparent 86px,
          #000 86px, #000 87px, transparent 87px, transparent 88px,
          #000 88px, #000 89px, transparent 89px, transparent 90px,
          #000 90px, #000 91px, transparent 91px, transparent 92px,
          #000 92px, #000 93px, transparent 93px, transparent 94px,
          #000 94px, #000 95px, transparent 95px, transparent 96px,
          #000 96px, #000 97px, transparent 97px, transparent 98px,
          #000 98px, #000 99px, transparent 99px, transparent 100px,
          #000 100px, #000 101px, transparent 101px, transparent 102px,
          #000 102px, #000 103px, transparent 103px, transparent 104px,
          #000 104px, #000 105px, transparent 105px, transparent 106px,
          #000 106px, #000 107px, transparent 107px, transparent 108px,
          #000 108px, #000 109px, transparent 109px, transparent 110px,
          #000 110px, #000 111px, transparent 111px, transparent 112px,
          #000 112px, #000 113px, transparent 113px, transparent 114px,
          #000 114px, #000 115px, transparent 115px, transparent 116px,
          #000 116px, #000 117px, transparent 117px, transparent 118px,
          #000 118px, #000 119px, transparent 119px, transparent 120px
        )`,
        height: '25px',
        width: '120px',
        display: 'inline-block',
        marginTop: '0px'
      };
    };

    return (
      <Modal
        title="打印入庫單"
        open={printModalVisible}
        onCancel={() => setPrintModalVisible(false)}
        footer={[
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={async () => {
            // 打開新窗口，從後端拉取 HTML 並列印
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
              message.error('無法打開列印窗口，請檢查瀏覽器彈窗設置');
              return;
            }
            printWindow.document.write(`<!DOCTYPE html><html><head><meta charset=\"utf-8\"><title>正在載入入庫憑證...</title></head><body style=\"font-family: Arial, \\\"Microsoft JhengHei\\\"; padding: 24px; color: #333;\">正在載入入庫憑證模板，請稍候...</body></html>`);
            printWindow.document.close();
            try {
              const html = await apiService.generateInboundVoucherHTML(selectedOrder?.orderNumber);
              printWindow.document.open();
              printWindow.document.write(html);
              printWindow.document.close();
              printWindow.onload = () => {
                setTimeout(() => {
                  printWindow.print();
                  printWindow.onafterprint = () => {
                    printWindow.close();
                  };
                }, 300);
              };
              setPrintModalVisible(false);
            } catch (e) {
              message.error('載入入庫憑證模板失敗');
              try { printWindow.close(); } catch {}
            }
          }}>
            打印
          </Button>
        ]}
        width={1000}
      >
        {selectedOrder && (
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            fontFamily: 'Arial, sans-serif',
            fontSize: '11px',
            lineHeight: '1.3',
            color: '#000',
            minHeight: '800px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {/* Header Section */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              marginBottom: '20px',
              paddingBottom: '10px',
              borderBottom: '1px solid #000'
            }}>
              {/* Left - Logo and Company Name */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '35px',
                  height: '35px',
                  backgroundColor: '#FFD700',
                  clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '10px',
                  border: '1px solid #000'
                }}>
                  <span style={{ color: '#000', fontSize: '14px', fontWeight: 'bold' }}>🐝</span>
                </div>
                <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#000' }}>DBEE</span>
              </div>
              
              {/* Center - Document Title */}
              <div style={{ textAlign: 'center', flex: '1' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#000' }}>入庫單</div>
              </div>
              
              {/* Right - Barcode */}
              <div style={{ textAlign: 'right' }}>
                <div style={generateBarcodeCSS(selectedOrder.barcode || selectedOrder.orderNumber)} />
                <div style={{ 
                  fontSize: '10px', 
                  color: '#000', 
                  fontFamily: 'monospace', 
                  marginTop: '2px', 
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {selectedOrder.barcode || selectedOrder.orderNumber}
                </div>
              </div>
            </div>

            {/* Main Content - Two Columns */}
            <div style={{ display: 'flex', gap: '25px', marginBottom: '20px' }}>
              {/* Left Column - Information */}
              <div style={{ flex: '1' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '15px',
                  marginLeft: '113px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#000'
                }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: '#007bff',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '8px',
                    color: 'white',
                    fontSize: '9px',
                    fontWeight: 'bold'
                  }}>
                    i
                  </div>
                  資訊
                </div>
                
                <div style={{ display: 'flex', marginBottom: '15px' }}>
                  {/* QR Code Section */}
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginRight: '15px'
                  }}>
                    <div style={{ 
                      width: '100px', 
                      height: '100px', 
                      backgroundColor: '#ffffff', 
                      border: '1px solid #dee2e6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px',
                      marginBottom: '5px'
                    }}>
                      {qrCodeDataUrl ? (
                        <img 
                          src={qrCodeDataUrl} 
                          alt="QR Code" 
                          style={{ 
                            width: '84px', 
                            height: '84px',
                            display: 'block'
                          }} 
                        />
                      ) : (
                        <div style={{
                          width: '84px',
                          height: '84px',
                          backgroundColor: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6c757d',
                          fontSize: '12px'
                        }}>
                          載入中...
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '9px', color: '#6c757d', textAlign: 'center' }}>
                      掃描QR碼查看入庫單
                    </div>
                  </div>
                  
                  {/* Information Fields */}
                  <div style={{ flex: '1' }}>
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>賬號:</span> 
                      <span style={{ marginLeft: '6px', fontSize: '11px' }}>{selectedOrder.createdBy || 'jasonhksong@gmail.com'}</span>
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>入庫單號:</span> 
                      <span style={{ marginLeft: '6px', fontFamily: 'monospace', fontSize: '11px' }}>{selectedOrder.orderNumber}</span>
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>建立時間:</span> 
                      <span style={{ marginLeft: '6px', fontSize: '11px' }}>{dayjs(selectedOrder.createdAt).format('YYYY-MM-DD HH:mm:ss')}</span>
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>物流方法:</span> 
                      <span style={{ marginLeft: '6px', fontSize: '11px' }}>{selectedOrder.logisticsMethod || '速遞公司'}</span>
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>物流追蹤號/參考資料:</span> 
                      <span style={{ marginLeft: '6px', fontFamily: 'monospace', fontSize: '11px' }}>{selectedOrder.trackingNumber || '順丰333223'}</span>
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>預計到達日期:</span> 
                      <span style={{ marginLeft: '6px', fontSize: '11px' }}>{dayjs(selectedOrder.expectedArrivalDate).format('YYYY-MM-DD')}</span>
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>備註:</span> 
                      <span style={{ marginLeft: '6px', fontSize: '11px' }}>{selectedOrder.cargoRemark || '1'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Inbound Data */}
              <div style={{ flex: '1' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '15px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#000'
                }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: '#007bff',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '8px',
                    color: 'white',
                    fontSize: '9px',
                    fontWeight: 'bold'
                  }}>
                    i
                  </div>
                  入倉資料
                </div>
                
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>貨主聯絡人名:</span> 
                  <span style={{ marginLeft: '6px', fontSize: '11px' }}>{selectedOrder.shipperContactName || 'qishuo zhu'}</span>
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>貨主聯絡人電話:</span> 
                  <span style={{ marginLeft: '6px', fontFamily: 'monospace', fontSize: '11px' }}>{selectedOrder.shipperContactPhone || '59317858'}</span>
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>收件人:</span> 
                  <span style={{ marginLeft: '6px', fontSize: '11px' }}>{selectedOrder.recipient || 'Delivery Bee'}</span>
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>收件地址:</span> 
                  <span style={{ marginLeft: '6px', fontSize: '11px' }}>
                    {selectedOrder.recipientAddress ? 
                      `${selectedOrder.recipientAddress.street}, ${selectedOrder.recipientAddress.city}` : 
                      '荃灣 柴灣角街66-82號 金熊工業中心C座18樓'}
                  </span>
                </div>
                <div style={{ fontSize: '8px', color: '#666', marginBottom: '6px', fontStyle: 'italic' }}>
                  (請留意所屬送貨位置)
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontWeight: 'bold', color: '#000', fontSize: '11px' }}>收件電話:</span> 
                  <span style={{ marginLeft: '6px', fontFamily: 'monospace', fontSize: '11px' }}>{selectedOrder.recipientPhone || '21568011'}</span>
                </div>
                <div style={{ fontSize: '12px', color: 'red', marginLeft: '180px', fontStyle: 'italic' }}>
                  *請列印此頁面,並張貼於貨物上。
                </div>
              </div>
            </div>

            {/* Inbound Goods Table */}
            <div style={{ marginBottom: '15px' }}>
              <div style={{ 
                backgroundColor: '#e9ecef', 
                padding: '6px', 
                fontWeight: 'bold',
                borderBottom: '1px solid #000',
                fontSize: '11px',
                color: '#000',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{ marginRight: '6px' }}>📦</span>
                入庫貨物
              </div>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '9px',
                border: '1px solid #000'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e9ecef' }}>#</th>
                    <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left', fontWeight: 'bold', backgroundColor: '#e9ecef' }}>貨物名稱</th>
                    <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left', fontWeight: 'bold', backgroundColor: '#e9ecef' }}>Barcode</th>
                    <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e9ecef' }}>預報數量</th>
                    <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left', fontWeight: 'bold', backgroundColor: '#e9ecef' }}>貨物備註</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items && selectedOrder.items.length > 0 ? (
                    selectedOrder.items.map((item, index) => (
                      <tr key={item.id}>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                          {index + 1}
                        </td>
                        <td style={{ border: '1px solid #000', padding: '4px' }}>
                          {item.productName}
                        </td>
                        <td style={{ border: '1px solid #000', padding: '4px', fontFamily: 'monospace' }}>
                          {item.productSku}
                        </td>
                        <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                          {item.expectedQuantity}
                        </td>
                        <td style={{ border: '1px solid #000', padding: '4px' }}>
                          {item.remark || '11'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>1</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>
                        {selectedOrder.customerName || '蘋果13 蘋果13'}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '4px', fontFamily: 'monospace' }}>
                        {selectedOrder.barcode || '32233243434'}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                        {selectedOrder.expectedQuantity || '100'}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>
                        {selectedOrder.cargoRemark || '11'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            
          </div>
        )}
      </Modal>
    );
  };


  const renderReceiptForm = () => (
    <Modal
      title="收貨"
      open={receiptModalVisible}
      onOk={() => receiptForm.submit()}
      onCancel={() => {
        setReceiptModalVisible(false);
        setSelectedReceiptOrder(null);
        setReceiptItems([]);
        receiptForm.resetFields();
      }}
      width={1000}
    >
      <Form
        form={receiptForm}
        layout="vertical"
        onFinish={handleSubmitReceipt}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="orderNumber"
              label="入庫單號"
            >
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="customer"
              label="客戶"
            >
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="warehouse"
              label="倉庫"
              rules={[{ required: true, message: '請選擇倉庫' }]}
            >
              <Select
                placeholder="選擇倉庫"
                onChange={(value) => {
                  // Fetch locations when warehouse changes
                  fetchWarehouseLocations(value);
                  // Clear current location in receipt item form if open
                  if (receiptItemForm && receiptItemModalVisible) {
                    receiptItemForm.setFieldsValue({ currentLocation: undefined });
                  }
                }}
              >
                {(warehouses.length ? warehouses : mockWarehouses).map(warehouse => (
                  <Option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="orderDate"
              label="發單日期"
            >
              <DatePicker style={{ width: '100%' }} disabled />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="receiptDate"
              label="收貨日期"
              rules={[{ required: true, message: '請選擇收貨日期' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="status"
              label="狀態"
              rules={[{ required: true, message: '請選擇狀態' }]}
            >
              <Select placeholder="選擇狀態">
                <Option value="received_quantity_correct">已收貨 數量正確</Option>
                <Option value="received_quantity_incorrect">已收貨 數量不正確</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>



        {/* Product Management Section */}
        <Divider orientation="left">收貨產品詳情</Divider>
        
        <div style={{ marginBottom: 16 }}>
          <Button 
            type="dashed" 
            icon={<PlusOutlined />} 
            onClick={handleAddReceiptItem}
            style={{ width: '100%' }}
          >
            添加產品
          </Button>
            </div>

        {receiptItems.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Alert
              message={`已添加 ${receiptItems.length} 個產品`}
              description={`預報總數量: ${receiptItems.reduce((sum, item) => sum + item.expectedQuantity, 0)} 件 | 確認總數量: ${receiptItems.reduce((sum, item) => sum + item.confirmedQuantity, 0)} 件`}
              type="info"
              showIcon
            />
          </div>
        )}

        {receiptItems.map((item, index) => (
          <Card 
            key={item.id} 
            size="small" 
            style={{ marginBottom: 8 }}
            extra={
              <Space>
                <Button 
                  type="link" 
                  size="small" 
                  icon={<EditOutlined />}
                  onClick={() => handleEditReceiptItem(index)}
                >
                  編輯
                </Button>
                <Popconfirm
                  title="確定要移除這個產品嗎？"
                  onConfirm={() => handleRemoveReceiptItem(index)}
                  okText="確定"
                  cancelText="取消"
                >
                  <Button 
                    type="link" 
                    size="small" 
                    danger 
                    icon={<DeleteOutlined />}
                  >
                    移除
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(120px, 1fr))', gap: 8, alignItems: 'center', minWidth: '1440px' }}>
                {/* 產品名稱 */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>產品名稱</Text>
                  <Text strong>{item.productName}</Text>
                </div>
                {/* SKU */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>SKU</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>{item.productSku}</Text>
                </div>
                {/* 預報數量 */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>預報數量</Text>
                  <InputNumber
                    size="small"
                    min={0}
                    value={item.expectedQuantity}
                    onChange={(value) => handleUpdateReceiptItemQuantity(index, 'expectedQuantity', value || 0)}
                    style={{ width: '100%' }}
                  />
                </div>
                {/* 確認數量 */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>確認數量</Text>
                  <InputNumber
                    size="small"
                    min={0}
                    value={item.confirmedQuantity}
                    onChange={(value) => handleUpdateReceiptItemQuantity(index, 'confirmedQuantity', value || 0)}
                    style={{ 
                      width: '100%',
                      borderColor: item.expectedQuantity !== item.confirmedQuantity ? '#ff4d4f' : undefined,
                      backgroundColor: item.expectedQuantity !== item.confirmedQuantity ? '#fff2f0' : undefined
                    }}
                  />
                </div>
                {/* 貨位 */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>貨位</Text>
                  <Text>{item.currentLocation ? (locationLabelMap[item.currentLocation] || item.currentLocation) : '-'}</Text>
                </div>
                {/* 庫存 */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>庫存</Text>
                  <Text>{item.currentStock}</Text>
                </div>
                {/* 長度 */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>長度 (cm)</Text>
                  <Text>{item.length ?? 'N/A'}</Text>
                </div>
                {/* 寬度 */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>寬度 (cm)</Text>
                  <Text>{item.width ?? 'N/A'}</Text>
                </div>
                {/* 高度 */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>高度 (cm)</Text>
                  <Text>{item.height ?? 'N/A'}</Text>
                </div>
                {/* 重量 */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>重量 (kg)</Text>
                  <Text>{item.weight}kg</Text>
                </div>
                {/* 批次 */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>批次</Text>
                  <Text>{item.lotNumber || 'N/A'}</Text>
                </div>
                {/* 序號 */}
                <div>
                  <Text strong style={{ fontSize: '12px', display: 'block' }}>序號</Text>
                  <Text>{item.serialNumber || 'N/A'}</Text>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </Form>
    </Modal>
  );

  const renderReceiptItemForm = () => (
    <Modal
      title={editingReceiptItemIndex >= 0 ? '編輯產品' : '添加產品'}
      open={receiptItemModalVisible}
      onOk={() => receiptItemForm.submit()}
      onCancel={() => {
        setReceiptItemModalVisible(false);
        setEditingReceiptItemIndex(-1);
        setSelectedReceiptItem(null);
        receiptItemForm.resetFields();
        // 重置數量差異狀態
        setQuantityDifferenceFields(new Set());
      }}
      width={800}
    >
      <Form
        form={receiptItemForm}
        layout="vertical"
        onFinish={handleSubmitReceiptItem}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="productSku"
              label="產品SKU"
              rules={[{ required: true, message: '請選擇產品SKU' }]}
            >
              <Select
                showSearch
                placeholder="請輸入產品SKU或名稱搜尋 (最少2個字)"
                loading={productsLoading}
                filterOption={false}
                onSearch={handleProductSearch}
                notFoundContent={productsLoading ? '搜尋中...' : '找不到產品，請輸入關鍵字搜尋'}
                options={products.map(product => ({
                  value: product.sku,
                  label: `${product.sku} - ${product.name}`,
                  product
                }))}
                onSelect={(value, option: any) => {
                  const product = option.product;
                  receiptItemForm.setFieldsValue({ productName: product.name });
                  // Reset products to initial state after selection
                  loadInitialProducts();
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="productName"
              label="產品名稱"
              rules={[{ required: true, message: '產品名稱將自動帶出' }]}
            >
              <Input readOnly placeholder="將自動帶出" />
            </Form.Item>
          </Col>
        </Row>

        {/* 移除了預報數量和確認數量字段，這些現在在收貨頁面直接管理 */}
        
        <Alert
          message="數量管理"
          description="預報數量和確認數量現在直接在收貨頁面的產品卡片中管理，無需在此編輯。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="length"
              label="長度 (cm)"
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="長度" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="width"
              label="寬度 (cm)"
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="寬度" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="height"
              label="高度 (cm)"
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="高度" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="weight"
              label="重量 (kg)"
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="重量" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="lotNumber"
              label="批次號"
            >
              <Input placeholder="批次號" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="serialNumber"
              label="序號"
            >
              <Input placeholder="序號" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="currentStock"
              label="現有庫存"
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="庫存數量" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="expiryDate"
              label="到期日期"
            >
              <DatePicker style={{ width: '100%' }} placeholder="選擇到期日期" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="baseUnit"
              label="基本單位"
              rules={[{ required: true, message: '請選擇基本單位' }]}
            >
              <Select placeholder="選擇基本單位" showSearch>
                {mockBaseUnits.map(unit => (
                  <Option key={unit.value} value={unit.value}>
                    {unit.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="currentLocation"
              label="現時貨位"
            >
              <TreeSelect
                style={{ width: '100%' }}
                placeholder={locationsLoading ? '載入中...' : locationTreeData.length === 0 ? '請先選擇入庫單倉庫' : '選擇貨位'}
                treeData={locationTreeData}
                treeLine
                showSearch
                allowClear
                disabled={locationsLoading || locationTreeData.length === 0}
                treeNodeLabelProp="displayLabel"
                treeNodeFilterProp="searchText"
                filterTreeNode={(input, treeNode) => {
                  const searchText = ((treeNode as any)?.searchText || '').toLowerCase();
                  return searchText.includes(input.toLowerCase());
                }}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                notFoundContent={locationsLoading ? '載入中...' : '無可用貨位'}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="remark"
          label="備註"
        >
          <Input.TextArea rows={2} placeholder="產品備註" />
        </Form.Item>

        {/* 系統字段 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="userName"
              label="操作用戶"
              tooltip="系統自動填充，顯示當前操作用戶"
            >
              <Input disabled placeholder="系統自動填充" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="updateDateTime"
              label="更新時間"
              tooltip="系統自動填充，顯示最後更新時間"
            >
              <Input disabled placeholder="系統自動填充" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* 標題和展開收起按鈕 */}
      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>Inbound</Title>
          <Text type="secondary">管理入庫訂單的創建、收貨和追蹤</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            type="text"
            icon={collapsed ? <DownOutlined /> : <UpOutlined />}
            onClick={toggleCollapsed}
            style={{ fontSize: 16 }}
          />
        </div>
      </div>

      {/* KPI 儀表板：根據 Tab 切換顯示 */}
      {!collapsed && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          {activeTab === 'overview' ? (
            <>
          <Col span={6}>
            <Card hoverable>
                  <Statistic title="總訂單數" value={statistics.totalOrders} prefix={<InboxOutlined />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable>
                  <Statistic title="即將到來" value={statistics.upcoming} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#1890ff' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable>
                  <Statistic title="待處理" value={statistics.pending} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#faad14' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable>
                  <Statistic title="已收貨" value={statistics.received} prefix={<TruckOutlined />} valueStyle={{ color: '#52c41a' }} />
            </Card>
          </Col>
            </>
          ) : (
            <>
              <Col span={6}>
                <Card hoverable>
                  <Statistic title="總訂單數" value={statistics.totalOrders} prefix={<InboxOutlined />} />
                </Card>
              </Col>
              <Col span={6}>
                <Card hoverable>
                  <Statistic title="已完成訂單" value={statistics.completed} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
                </Card>
              </Col>
              <Col span={6}>
                <Card hoverable>
                  <Statistic title="總價值" value={`HKD ${statistics.totalValue.toLocaleString()}`} prefix={"$" as any} />
                </Card>
              </Col>
              <Col span={6}>
                <Card hoverable>
                  <Statistic title="已選擇" value={statistics.selectedCount} prefix={<CheckCircleOutlined />} />
                </Card>
              </Col>
            </>
          )}
        </Row>
      )}
      {/* 添加異常行樣式 */}
      <style>
        {`
          .inbound-row-anomaly {
            background-color: #fff2f0 !important;
            border-left: 4px solid #ff4d4f !important;
          }
          .inbound-row-anomaly:hover {
            background-color: #ffebe8 !important;
          }
          .inbound-row-anomaly td {
            border-bottom-color: #ffccc7 !important;
          }
        `}
      </style>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <span>
              <FileTextOutlined />
              {!isMobile && "訂單概覽"}
            </span>
          } 
          key="overview"
        >
          <Card>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateOrder}
                  {...getMobileButtonProps(isMobile)}
                >
                  {!isMobile && "預定入庫"}
                </Button>
                <Button
                  type="default"
                  icon={<UploadOutlined />}
                  onClick={handleBulkUpload}
                  {...getMobileButtonProps(isMobile)}
                >
                  {!isMobile && "批量上傳"}
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadInboundData}
                  loading={loading}
                  {...getMobileButtonProps(isMobile)}
                >
                  {!isMobile && "刷新"}
                </Button>
                <Button
                  type="default"
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  {...getMobileButtonProps(isMobile)}
                >
                  {!isMobile && "導出"}
                </Button>
              </Space>
              <Space>
                <Search
                  placeholder="搜索訂單..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={() => {
                    // 只在點擊搜索按鈕或按 Enter 時觸發查詢
                    setCurrentPage(1);
                    loadInboundData(1, pageSize);
                  }}
                  style={{ width: 300 }}
                  allowClear
                />
                <Dropdown 
                  overlay={columnSettingsMenu} 
                  trigger={['click']}
                  open={columnSettingsVisible}
                  onOpenChange={setColumnSettingsVisible}
                >
                  <Button icon={<SettingOutlined />} title="列設置">
                    {!isMobile && "列設置"}
                  </Button>
                </Dropdown>
              </Space>
            </div>

            <Table
              columns={draggableColumns as any}
              dataSource={sortedAndFilteredOrders}
              rowKey="id"
              loading={loading}
              scroll={{ x: 'max-content' }}
              rowClassName={(record) => {
                // 整單異常標示邏輯：有數量異常的入庫單整行標示為紅色
                return hasQuantityAnomaly(record) ? 'inbound-row-anomaly' : '';
              }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: total,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 500],
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 條，共 ${total} 條記錄`,
                showLessItems: true,
                onChange: (page, newPageSize) => {
                  if (newPageSize && newPageSize !== pageSize) {
                    // 改變每頁條數，重置到第一頁
                    setPageSize(newPageSize);
                    setCurrentPage(1);
                    loadInboundData(1, newPageSize);
                  } else {
                    // 只改變頁碼
                    setCurrentPage(page);
                    loadInboundData(page, pageSize);
                  }
                },
              }}
            />
          </Card>
        </TabPane>

        {/*<TabPane
          tab={
            <span>
              <UploadOutlined />
              {!isMobile && "批量上傳"}
            </span>
          } 
          key="upload"
        >
          <Card>
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <UploadOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 24 }} />
              <Title level={3}>批量上傳入庫訂單</Title>
              <Paragraph type="secondary" style={{ fontSize: 16, marginBottom: 32 }}>
                使用統一的批量上傳工具，支持CSV格式文件上傳
              </Paragraph>
              
                        <Button 
                          type="primary" 
                          icon={<UploadOutlined />}
                          size="large"
                onClick={() => window.location.href = '/inventory/inbound/bulk-upload'}
                style={{ height: 48, fontSize: 16, minWidth: 160 }}
                        >
                開始批量上傳
                        </Button>
              
              <div style={{ marginTop: 24 }}>
                    <Text type="secondary">
                  支持CSV格式 • 自動數據驗證 • 錯誤檢查 • 統一用戶界面
                    </Text>
                    </div>
                  </div>


          </Card>
        </TabPane>*/}

      </Tabs>

      {/* 模態框 */}
      {renderOrderForm()}
      {renderItemForm()}
      {renderPrintModal()}
      {renderFieldConfigModal()}
      {renderViewOrderModal()}
      {renderReceiptForm()}
      {renderReceiptItemForm()}
      
      {/* 批量上傳模態框 */}
      <InboundBulkUploadModal
        visible={bulkUploadModalVisible}
        onCancel={() => setBulkUploadModalVisible(false)}
        onUploadComplete={handleBulkUploadComplete}
      />
    </div>
  );
};

export default InboundManagement; 